import { useEffect } from "react";
import {
  ArrowRight,
  Building2,
  Car,
  CheckCircle2,
  Clock,
  CreditCard,
  Euro,
  ExternalLink,
  FileText,
  KeyRound,
  Mail,
  MapPin,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
  Sparkles,
  Star,
  Unlock,
  Wrench,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import routeSeoData from "@/seo/routeSeo.json";

const staticInfo = {
  name: "Schlüsseldienst Notdienst 24h",
  phone: "0152 5786 8391",
  phone_href: "tel:+4915257868391",
  whatsapp_href:
    "https://wa.me/4915257868391?text=Hallo%2C%20ich%20ben%C3%B6tige%20Hilfe%20von%20einem%20Schl%C3%BCsseldienst%20in%20D%C3%BCren.",
  address: "Wirtelstraße 38–42, 52349 Düren",
  city: "Düren",
  operator_name: "Fouad AL Qaisi",
  legal_company: "Schlüsseldienst am Kaufhof",
  main_address: "Wirtelstraße 38–42, 52349 Düren",
  branch_address: "Kölner Landstraße 175, 52351 Düren",
  email: "mahanalqaisi@gmail.com",
  google_maps_url: "https://maps.app.goo.gl/sfSXF2P7QTWdCBQc6",
  google_rating: "5,0",
  google_review_count: 23,
  google_rating_checked_at: "21. August 2026",
  hero_claim: "24h erreichbar. Typische Anfahrt in Düren: 15–30 Minuten.",
};

const publicPath = (path) => {
  if (!path || !path.startsWith("/") || path === "/" || path.startsWith("/#") || path.endsWith("/")) {
    return path;
  }
  return `${path}/`;
};

const services = [
  {
    path: "/tueroeffnung-dueren",
    title: "Türöffnung",
    description:
      "Ausgesperrt? Wir helfen schnell bei zugefallenen und verschlossenen Türen – sauber, diskret und möglichst beschädigungsfrei.",
    icon: Unlock,
    testId: "service-card-tueroeffnung",
    label: "Wohnung, Haus & Gewerbe",
  },
  {
    path: "/schlosswechsel-dueren",
    title: "Schlosswechsel",
    description:
      "Defektes Schloss, Schlüssel verloren oder Sicherheitsgefühl verloren? Wir wechseln Zylinder und Schlösser fachgerecht vor Ort.",
    icon: Wrench,
    testId: "service-card-schlosswechsel",
    label: "Zylinder & Sicherheit",
  },
  {
    path: "/autooeffnung-dueren",
    title: "Autoschlüssel & Autoöffnung",
    description:
      "Hilfe bei Problemen rund ums Fahrzeug: ausgesperrt, Schlüssel im Auto oder Autoschlüssel-Thema schnell klären lassen.",
    icon: Car,
    testId: "service-card-autoschluessel",
    label: "Auto & Schlüsselhilfe",
  },
];

const trustStats = [
  { value: "24h", label: "täglich erreichbar", icon: Clock, testId: "trust-stat-24h" },
  { value: "15–30 Min.", label: "typische Anfahrt in Düren", icon: MapPin, testId: "trust-stat-arrival" },
  { value: "ab 79,- €", label: "Türöffnung transparent", icon: Euro, testId: "trust-stat-price" },
  { value: "Festpreis", label: "vorab am Telefon", icon: ShieldCheck, testId: "trust-stat-fixed-price" },
];

const priceRows = [
  { label: "Türöffnung zugefallen", price: "ab 79,- €", note: "verbindlicher Festpreis vor der Anfahrt", testId: "pricing-row-door" },
  { label: "Schlosswechsel", price: "Festpreis", note: "nach telefonischer Klärung des passenden Zylinders", testId: "pricing-row-lock" },
  { label: "Autoschlüssel / Autoöffnung", price: "auf Anfrage", note: "je nach Fahrzeug und Situation", testId: "pricing-row-car" },
];

const serviceAreas = [
  { label: "Düren Zentrum", path: "/schluesseldienst-dueren-zentrum" },
  { label: "Birkesdorf", path: "/schluesseldienst-birkesdorf" },
  { label: "Mariaweiler", path: "/schluesseldienst-mariaweiler" },
  { label: "Lendersdorf", path: "/schluesseldienst-lendersdorf" },
  { label: "Niederau", path: "/schluesseldienst-niederau" },
  { label: "Kreuzau", path: "/schluesseldienst-kreuzau" },
  { label: "Merzenich", path: "/schluesseldienst-merzenich" },
  { label: "Jülich", path: "/schluesseldienst-juelich" },
  { label: "Kerpen", path: "/schluesseldienst-kerpen" },
  { label: "Eschweiler", path: "/schluesseldienst-eschweiler" },
  { label: "Hürtgenwald", path: "/schluesseldienst-huertgenwald" },
  { label: "Umgebung Düren", path: null },
];

const customerVoices = [
  {
    name: "M. Yilmaz",
    area: "Düren-Zentrum",
    text: "Sehr freundlich am Telefon, Preis vorher erklärt und die Tür war schnell wieder offen.",
  },
  {
    name: "A. Becker",
    area: "Birkesdorf",
    text: "Kam zügig vorbei und hat sauber gearbeitet. Besonders gut: keine Überraschung beim Preis.",
  },
  {
    name: "N. Haddad",
    area: "Mariaweiler",
    text: "Nach Schlüsselverlust wurde das Schloss direkt gewechselt. Ruhige Beratung und faire Abwicklung.",
  },
  {
    name: "K. Nowak",
    area: "Lendersdorf",
    text: "Telefonisch direkt erreichbar, klare Aussage zum Festpreis und schnelle Hilfe am Abend.",
  },
  {
    name: "S. El Amrani",
    area: "Kreuzau",
    text: "Seriöser Eindruck, gute Erklärung vor Ort und die Rechnung war nachvollziehbar.",
  },
  {
    name: "L. Schäfer",
    area: "Merzenich",
    text: "Bei der Autoöffnung wurde vorsichtig gearbeitet. Genau so wünscht man sich Hilfe im Notfall.",
  },
];

const faqs = [
  {
    question: "Was kostet eine Türöffnung in Düren?",
    answer:
      "Eine einfache Türöffnung startet bei uns ab 79,- €. Den verbindlichen Festpreis vereinbaren wir vorab telefonisch, damit Sie keine unangenehmen Überraschungen erleben.",
  },
  {
    question: "Wie schnell ist der Schlüsseldienst in Düren vor Ort?",
    answer:
      "Die typische Anfahrt innerhalb Dürens beträgt 15–30 Minuten. Die voraussichtliche Anfahrtszeit für Ihre konkrete Adresse nennen wir Ihnen direkt am Telefon.",
  },
  {
    question: "Wird meine Tür beschädigt?",
    answer:
      "Bei zugefallenen Türen ist eine beschädigungsfreie Öffnung in den meisten Fällen möglich. Sollte ein Sonderfall vorliegen, erklären wir die Optionen vorher transparent.",
  },
  {
    question: "Ist der telefonisch vereinbarte Festpreis verbindlich?",
    answer:
      "Ja. Sie schildern uns die genaue Adresse und Türsituation. Für den beschriebenen Auftrag vereinbaren wir vor der Anfahrt einen verbindlichen Festpreis.",
  },
  {
    question: "Kann ich mit EC- oder Kreditkarte bezahlen?",
    answer:
      "Ja. Wir akzeptieren EC- und Kreditkartenzahlung.",
  },
  {
    question: "Muss ich nachweisen, dass ich die Tür öffnen lassen darf?",
    answer:
      "Ja. Eine Tür- oder Fahrzeugöffnung darf nur für berechtigte Personen erfolgen. Halten Sie deshalb einen Ausweis oder einen anderen geeigneten Nachweis bereit.",
  },
  {
    question: "Kann ein defektes Schloss direkt gewechselt werden?",
    answer:
      "Ein Schloss- oder Zylinderwechsel gehört zum Leistungsbereich. Ob ein passendes Teil direkt verfügbar ist und welcher Aufwand entsteht, lässt sich nach Angaben zu Tür und Schloss beziehungsweise vor Ort beurteilen.",
  },
  {
    question: "Was bedeutet 24h erreichbar?",
    answer:
      "Die Telefonnummer des Schlüsselnotdienstes ist 24 Stunden erreichbar. Das ist von den Öffnungszeiten der Geschäftsadressen zu unterscheiden. Ob ein mobiler Einsatz möglich ist, klären wir für Ihre Adresse direkt am Telefon.",
  },
  {
    question: "Welche Orte werden angefahren?",
    answer:
      "Düren und die auf der Website genannten Einsatzgebiete werden nach telefonischer Prüfung bedient. Die typische Anfahrt von 15–30 Minuten bezieht sich auf Düren; für weiter entfernte Orte nennen wir Verfügbarkeit und voraussichtliche Anfahrt individuell.",
  },
];

const serviceSeoPages = [
  {
    path: "/tueroeffnung-dueren",
    title: "Türöffnung Düren",
    headline: "Türöffnung in Düren",
    description: "Zugefallene oder verschlossene Tür in Düren? Wir klären Türzustand, Anfahrt und den verbindlichen Festpreis vorab. Eine einfache Türöffnung startet ab 79,- €.",
    keyword: "Türöffnung Düren",
    icon: Unlock,
    points: ["Zugefallene Wohnungstüren", "Haus- und Kellertüren", "Preis vor Anfahrt klären", "24h telefonisch erreichbar"],
    fact: {
      label: "Einfache Türöffnung",
      value: "ab 79,- €",
      note: "Für den am Telefon beschriebenen Auftrag vereinbaren wir den verbindlichen Festpreis vor der Anfahrt.",
    },
    paragraphs: [
      "Wenn eine Wohnungs-, Haus- oder Gewerbetür in Düren plötzlich zu ist, zählt ein klarer Ablauf. Nennen Sie am Telefon die vollständige Adresse, ob die Tür nur zugefallen oder abgeschlossen ist, ob innen ein Schlüssel steckt und ob ein Defekt erkennbar ist.",
      "Bei einer lediglich zugefallenen Tür ist eine schonende Öffnung in vielen Fällen möglich. Eine Garantie wäre unseriös, denn Türblatt, Beschlag, Schließzylinder und mögliche Vorschäden beeinflussen die geeignete Methode. Wir erklären das geplante Vorgehen, bevor gearbeitet wird.",
      "Vor der Öffnung muss feststehen, dass Sie zum Zutritt berechtigt sind. Halten Sie einen Ausweis oder einen anderen geeigneten Nachweis bereit. Liegt das Dokument in der Wohnung, kann die Identität unmittelbar nach der Öffnung geprüft werden.",
    ],
    details: [
      ["Zugefallen oder abgeschlossen?", "Diese Unterscheidung beeinflusst Aufwand und Öffnungsmethode. Sagen Sie außerdem, ob ein Schlüssel innen steckt, abgebrochen ist oder sich der Zylinder noch drehen lässt."],
      ["Preis vor der Anfahrt", "Die einfache Türöffnung beginnt bei 79,- €. Für den konkret beschriebenen Auftrag werden Verfügbarkeit, Anfahrt und verbindlicher Festpreis telefonisch festgehalten, bevor jemand losfährt."],
      ["Nach der Öffnung", "Ist Schloss oder Zylinder defekt, besprechen wir einen möglichen Austausch getrennt. Material wird nicht ohne Erklärung eingebaut; Umfang und Preis einer Zusatzleistung werden vorher geklärt."],
    ],
    faqs: [
      ["Kann eine zugefallene Tür ohne Schaden geöffnet werden?", "Das ist bei vielen zugefallenen Türen möglich, hängt aber von Tür, Beschlag, Schloss und vorhandenen Schäden ab. Wir formulieren deshalb keine pauschale Garantie und erklären Sonderfälle vor der Arbeit."],
      ["Was soll ich beim Anruf zur Tür sagen?", "Wichtig sind Adresse, Türart, zugefallen oder abgeschlossen, Position des Schlüssels und erkennbare Defekte. Je genauer die Angaben sind, desto belastbarer ist die telefonische Preisabsprache."],
      ["Wer darf eine Türöffnung beauftragen?", "Nur eine berechtigte Person. Vor beziehungsweise unmittelbar nach der Öffnung wird die Berechtigung anhand eines geeigneten Nachweises geprüft."],
    ],
  },
  {
    path: "/schlosswechsel-dueren",
    title: "Schlosswechsel Düren",
    headline: "Schlosswechsel in Düren",
    description: "Schloss oder Profilzylinder in Düren wechseln lassen: bei Schlüsselverlust, Defekt oder Sicherheitsbedenken. Aufwand und Material werden vor der Beauftragung erklärt.",
    keyword: "Schlosswechsel Düren",
    icon: Wrench,
    points: ["Zylindertausch", "Schlüssel verloren", "Defektes Schloss", "Sicherheitsberatung vor Ort"],
    fact: {
      label: "Preisgrundlage",
      value: "Schloss & Material",
      note: "Der Preis hängt vom benötigten Bauteil und der Einbausituation ab und wird vor dem Austausch geklärt.",
    },
    paragraphs: [
      "Ein Schloss- oder Zylinderwechsel kann nach einem Schlüsselverlust, bei einem klemmenden Zylinder, nach einem Einbruchversuch oder bei berechtigten Sicherheitsbedenken sinnvoll sein. Nicht jedes Problem erfordert automatisch den Austausch des kompletten Schlosses.",
      "Für eine erste Einschätzung helfen Fotos und Angaben zu Tür, Beschlag, Zylinder und Schlüssel. Vor Ort wird geprüft, welches Bauteil tatsächlich betroffen ist. Erst dann wird besprochen, welches Material passt und welche Arbeit erforderlich ist.",
      "Bei einer Mietwohnung sollte geklärt werden, wer den Auftrag erteilen darf und ob Vermieter oder Hausverwaltung informiert werden müssen. Nach einem Diebstahl können außerdem Polizei und Versicherung relevant sein.",
    ],
    details: [
      ["Zylinder oder komplettes Schloss?", "Ein austauschbarer Profilzylinder ist nicht dasselbe wie das Einsteckschloss in der Tür. Wir erklären, welches Teil betroffen ist, damit kein unnötiger Komplettaustausch beauftragt wird."],
      ["Passendes Maß und Sicherheitsniveau", "Länge, Türstärke, Beschlag und gewünschter Schutz bestimmen den passenden Zylinder. Ein Bauteil sollte nicht deutlich über den Beschlag hinausragen."],
      ["Kosten nachvollziehbar abstimmen", "Arbeitsaufwand und Material werden getrennt betrachtet. Ein konkreter Festpreis ist erst möglich, wenn Tür, vorhandenes Schloss und gewünschte Ausführung ausreichend beschrieben sind."],
    ],
    faqs: [
      ["Muss nach jedem Schlüsselverlust das Schloss gewechselt werden?", "Nein. Entscheidend ist, ob der Schlüssel einem Objekt zugeordnet werden kann und ob Missbrauch realistisch ist. Bei Verlust zusammen mit Adresse oder Papieren ist ein Wechsel eher zu erwägen."],
      ["Kann nur der Schließzylinder gewechselt werden?", "Bei vielen Türen ja. Ob der Zylinder allein Ursache und austauschbar ist, hängt von Tür, Beschlag und Schloss ab und wird vor Ort geprüft."],
      ["Ist ein passender Zylinder immer sofort verfügbar?", "Das lässt sich nicht pauschal versprechen. Maße, Bauform und gewünschte Sicherheitsmerkmale entscheiden über die Verfügbarkeit. Wir klären bekannte Daten vor der Anfahrt."],
    ],
  },
  {
    path: "/autooeffnung-dueren",
    title: "Autoöffnung Düren",
    headline: "Autoöffnung in Düren",
    description: "Fahrzeug verschlossen und Schlüssel im Auto? Für eine Autoöffnung in Düren prüfen wir Fahrzeugdaten, Standort, Berechtigung und mögliche Vorgehensweise vorab telefonisch.",
    keyword: "Autoöffnung Düren",
    icon: Car,
    points: ["Schlüssel im Auto", "Fahrzeug verschlossen", "Schonendes Vorgehen", "Situation telefonisch klären"],
    fact: {
      label: "Vorab erforderlich",
      value: "Fahrzeugdaten",
      note: "Modell, Baujahr, Standort und konkrete Schlüsselsituation bestimmen, ob und wie eine Öffnung möglich ist.",
    },
    paragraphs: [
      "Liegt der Schlüssel sichtbar im Fahrzeug oder ist das Auto versehentlich verriegelt, sollte nicht mit Draht, Schraubendreher oder Hebelwerkzeug improvisiert werden. Solche Versuche können Dichtungen, Lack, Elektronik oder Airbagsysteme beschädigen.",
      "Nennen Sie beim Anruf Hersteller, Modell, möglichst das Baujahr, den Standort des Fahrzeugs und die Position des Schlüssels. Daraus ergibt sich, ob eine mobile Öffnung geprüft werden kann und welche Vorgehensweise infrage kommt.",
      "Eine Fahrzeugöffnung erfolgt nur nach Berechtigungsprüfung. Halten Sie Fahrzeugpapiere und Ausweis bereit, soweit sie zugänglich sind. Befinden sich die Dokumente im Auto, wird die Berechtigung unmittelbar nach der Öffnung kontrolliert.",
    ],
    details: [
      ["Keine pauschale Methode für jedes Auto", "Türkonstruktion, rahmenlose Scheiben, Doppelverriegelung und elektronische Systeme unterscheiden sich. Deshalb wird die Methode nicht allein anhand des Ortsnamens oder eines Lockpreises festgelegt."],
      ["Beschädigungen nicht selbst provozieren", "Ziehen Sie keine Drähte durch Dichtungen und hebeln Sie den Türrahmen nicht auf. Bleiben Sie telefonisch erreichbar und sichern Sie das Fahrzeug, ohne den Verkehr zu behindern."],
      ["Kosten und Machbarkeit", "Ein Preis wird erst nach den Fahrzeug- und Situationsangaben verbindlich vereinbart. Kann die konkrete Leistung nicht verantwortbar angeboten werden, wird keine Öffnung versprochen."],
    ],
    faqs: [
      ["Welche Angaben brauchen Sie für eine Autoöffnung?", "Hersteller, Modell, Baujahr soweit bekannt, genauer Standort, Position des Schlüssels und Informationen zu eventuell bereits unternommenen Öffnungsversuchen."],
      ["Öffnen Sie jedes Fahrzeugmodell?", "Nein pauschales Versprechen wäre unseriös. Machbarkeit und geeignetes Vorgehen werden für das konkrete Fahrzeug geprüft."],
      ["Muss ich nachweisen, dass das Auto mir gehört?", "Ja. Eine Öffnung darf nur für eine berechtigte Person erfolgen. Geeignete Fahrzeug- und Identitätsnachweise müssen geprüft werden."],
    ],
  },
  {
    path: "/autoschluessel-dueren",
    title: "Autoschlüssel Düren",
    headline: "Hilfe bei Autoschlüsseln in Düren",
    description: "Autoschlüssel verloren, beschädigt oder im Fahrzeug? Wir prüfen telefonisch, ob Autoöffnung oder eine andere Hilfe möglich ist. Keine pauschale Zusage für Nachfertigung oder Programmierung.",
    keyword: "Autoschlüssel Düren",
    icon: KeyRound,
    points: ["Autoschlüssel verloren", "Schlüssel im Fahrzeug", "Autoöffnung prüfen", "Leistungsgrenzen offen benennen"],
    fact: {
      label: "Leistungsprüfung",
      value: "Daten & Foto",
      note: "Fahrzeug- und Schlüsseltyp müssen vor einer Zusage geprüft werden; nicht jeder Schlüssel kann nachgefertigt oder programmiert werden.",
    },
    paragraphs: [
      "Der Begriff Autoschlüssel-Hilfe umfasst unterschiedliche Fälle: Der Schlüssel kann im verriegelten Fahrzeug liegen, mechanisch beschädigt sein, verloren gegangen sein oder von der Wegfahrsperre nicht erkannt werden. Diese Situationen benötigen unterschiedliche Lösungen.",
      "Senden oder nennen Sie Hersteller, Modell, Baujahr, Schlüsselart und das genaue Problem. Ein Foto des vorhandenen Schlüssels und der Fahrzeugpapiere kann die Prüfung erleichtern. Sensible Daten sollten nur über den vereinbarten Kontaktweg übermittelt werden.",
      "Wir versprechen nicht pauschal, jeden Fahrzeugschlüssel zu kopieren, zu fräsen oder zu programmieren. Wenn ausschließlich eine berechtigte Fahrzeugöffnung möglich ist, wird das klar von einer Schlüssel-Nachfertigung getrennt.",
    ],
    details: [
      ["Autoöffnung ist nicht Nachfertigung", "Liegt der Schlüssel im Auto, kann eine Fahrzeugöffnung das eigentliche Problem lösen. Bei vollständigem Verlust wird dagegen ein passender Ersatzschlüssel samt möglicher Programmierung benötigt."],
      ["Diese Angaben erleichtern die Prüfung", "Hilfreich sind Fahrzeugidentifikation, Baujahr, Schlüsseltyp, Anzahl vorhandener Schlüssel, Fehlerbeschreibung und gut erkennbare Fotos. Daraus entsteht noch keine automatische Leistungszusage."],
      ["Berechtigung und Sicherheit", "Auch bei Schlüsselproblemen wird die Berechtigung am Fahrzeug geprüft. Codes, Transponder- oder Wegfahrsperrendaten werden nicht ohne nachvollziehbaren Eigentums- beziehungsweise Nutzungsnachweis bearbeitet."],
    ],
    faqs: [
      ["Fertigen Sie jeden Autoschlüssel nach?", "Nein. Schlüsselprofil, Transponder, Wegfahrsperre, Fahrzeugmodell und verfügbare Technik entscheiden, ob eine Nachfertigung oder Programmierung angeboten werden kann."],
      ["Was ist, wenn der Schlüssel nur im Auto liegt?", "Dann geht es in erster Linie um eine Autoöffnung. Fahrzeugdaten, Standort, Situation und Berechtigung werden vor einer Zusage geprüft."],
      ["Welche Unterlagen werden benötigt?", "In der Regel geeignete Identitäts- und Fahrzeugnachweise. Welche Unterlagen im Einzelfall erforderlich sind, wird bei der telefonischen Prüfung genannt."],
    ],
  },
  {
    path: "/schluesselnotdienst-dueren",
    title: "Schlüsselnotdienst Düren 24h",
    headline: "Schlüsselnotdienst in Düren – 24h telefonisch erreichbar",
    description: "Akuter Schlüsselnotfall in Düren: 24h telefonisch erreichbar für Tür-, Schloss- und Fahrzeugprobleme. Verfügbarkeit, Anfahrt und verbindlichen Festpreis vorab klären.",
    keyword: "Schlüsselnotdienst Düren",
    icon: Clock,
    points: ["24 Stunden erreichbar", "Auch nachts und am Wochenende", "Türöffnung ab 79,- €", "Keine anonyme Vermittlung"],
    fact: {
      label: "Erreichbarkeit",
      value: "24h telefonisch",
      note: "Die 24h-Angabe bezieht sich auf den Schlüsselnotdienst und nicht auf die Öffnungszeiten der Ladengeschäfte.",
    },
    paragraphs: [
      "Ein Schlüsselnotfall kann nachts, am Wochenende oder an einem Feiertag entstehen. Über die angegebene Telefonnummer ist der Notdienst 24 Stunden erreichbar. Am Telefon wird zunächst geklärt, was passiert ist und ob ein mobiler Einsatz für die konkrete Adresse möglich ist.",
      "Beschreiben Sie, ob eine Tür zugefallen oder abgeschlossen ist, ein Schlüssel fehlt oder abgebrochen ist, ein Schloss defekt wirkt oder ein Fahrzeug betroffen ist. Erst nach dieser Einordnung werden Vorgehen, voraussichtliche Anfahrt und verbindlicher Festpreis besprochen.",
      "Die 24h-Erreichbarkeit ist keine Aussage über durchgehend geöffnete Ladenlokale. Sie beschreibt den telefonischen Schlüsselnotdienst. Für Einsätze außerhalb Dürens gelten keine pauschalen 15–30 Minuten; dort wird die Anfahrt individuell genannt.",
    ],
    details: [
      ["Akute Gefahr hat Vorrang", "Sind ein Kind oder eine hilflose Person eingeschlossen, brennt ein Herd oder besteht eine andere unmittelbare Gefahr, rufen Sie zuerst 112. Ein Schlüsseldienst ersetzt keine Feuerwehr, Polizei oder medizinische Hilfe."],
      ["Festpreis vor dem Losfahren", "Für die geschilderte Situation wird vor der Anfahrt ein verbindlicher Preis vereinbart. Verändert sich die Lage oder zeigt sich ein nicht beschriebener Defekt, wird zusätzlicher Aufwand vor weiterer Arbeit erklärt."],
      ["Berechtigungsprüfung auch nachts", "Zeitdruck ändert nichts an der Pflicht, die Berechtigung zu prüfen. Halten Sie Ausweis, Miet- oder Fahrzeugnachweis beziehungsweise eine andere geeignete Bestätigung bereit."],
    ],
    faqs: [
      ["Ist der Schlüsselnotdienst wirklich 24 Stunden erreichbar?", "Ja, die telefonische Notdienst-Erreichbarkeit ist für 24 Stunden bestätigt. Sie ist von den Öffnungszeiten der beiden Geschäftsadressen zu unterscheiden."],
      ["Gelten 15–30 Minuten auch nachts und außerhalb Dürens?", "Die Angabe ist eine typische Anfahrt innerhalb Dürens. Verkehr, Wetter, Auslastung und Adresse können abweichen; für andere Orte wird die Zeit individuell genannt."],
      ["Was passiert bei einer gefährlichen Notlage?", "Bei unmittelbarer Gefahr für Menschen oder Tiere sowie bei Brandgefahr gilt: zuerst 112 anrufen und den Anweisungen der Leitstelle folgen."],
    ],
  },
];

const areaSeoPages = [
  {
    path: "/schluesseldienst-birkesdorf",
    area: "Birkesdorf",
    travel: "im Nordosten Dürens zwischen Rur, A4 und der Grenze zu Niederzier",
    focus: "Wohnungen, Häuser und Gewerbeobjekte",
    intro:
      "Birkesdorf liegt im Nordosten Dürens und ist baulich mit dem übrigen Stadtgebiet verbunden. Für Türöffnungen, Schlossprobleme und die weiteren angebotenen Leistungen ist ein mobiler Einsatz nach telefonischer Abstimmung möglich.",
    localNote:
      "Diese Seite beschreibt Birkesdorf als Einsatzgebiet. Es wird keine Filiale in Birkesdorf behauptet. Für eine verlässliche Zuordnung nennen Sie bitte Straße, Hausnummer und die betroffene Tür.",
    planning:
      "Für Birkesdorf hilft zusätzlich die Angabe, ob sich die Adresse im zusammenhängenden Wohngebiet, nahe der Rur oder im Übergang Richtung Niederzier befindet. Bei Mehrfamilienhäusern benötigen wir Eingang, Etage und Klingelname.",
    localFacts: ["Birkesdorf liegt westlich der Rur und südlich der A4.", "In Richtung Norden grenzt der Stadtteil an das Gebiet von Niederzier."],
    nearbyLinks: [
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
      ["Mariaweiler", "/schluesseldienst-mariaweiler"],
    ],
    faqs: [
      ["Gibt es einen Standort des Schlüsseldienstes in Birkesdorf?", "Nein. Birkesdorf wird als mobiles Einsatzgebiet beschrieben. Anfahrt, Verfügbarkeit und Festpreis werden für die konkrete Adresse telefonisch abgestimmt."],
      ["Welche Angaben brauchen Sie für einen Einsatz in Birkesdorf?", "Nennen Sie die vollständige Anschrift, ob die Tür zugefallen oder abgeschlossen ist und wo sich der Schlüssel befindet. Vor der Öffnung wird außerdem die Berechtigung geprüft."],
    ],
    officialUrl: "https://www.dueren.de/kultur-tourismus/stadtportraet/stadtteile/birkesdorf",
  },
  {
    path: "/schluesseldienst-mariaweiler",
    area: "Mariaweiler",
    travel: "am nordwestlichen Stadtrand zwischen Rur und Dürener Badesee",
    focus: "Türöffnungen und Schlosswechsel nach Schlüsselverlust",
    intro:
      "Mariaweiler liegt am nordwestlichen Stadtrand Dürens zwischen der Rur und dem Bereich des Dürener Badesees. Der mobile Schlüsseldienst fährt nach telefonischer Abstimmung zu Adressen in Mariaweiler.",
    localNote:
      "Mariaweiler ist ein Einsatzgebiet und keine Niederlassung. Bei Wohnanlagen helfen genaue Angaben zu Gebäudeeingang, Etage und Tür dabei, den Auftrag vor der Anfahrt eindeutig zu klären.",
    planning:
      "Bei Anfragen aus Mariaweiler nennen Sie bitte auch, ob ein einzelnes Wohnhaus, eine größere Wohnanlage oder ein Gewerbeobjekt betroffen ist. So lassen sich Zugang, Berechtigungsprüfung und benötigte Angaben vorab strukturieren.",
    localFacts: ["Westlich von Mariaweiler liegt der Dürener Badesee.", "Birkesdorf, Gürzenich, Echtz und Merken liegen im nahen Umfeld."],
    nearbyLinks: [
      ["Birkesdorf", "/schluesseldienst-birkesdorf"],
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
    ],
    faqs: [
      ["Welche Ortsangabe ist bei einem Einsatz in Mariaweiler wichtig?", "Neben Straße und Hausnummer helfen Angaben zur Wohnanlage, zum Gebäudeeingang und zur betroffenen Tür. So kann die Situation telefonisch sauber eingeschätzt werden."],
      ["Deckt die Mariaweiler-Seite auch andere Stadtteile ab?", "Mariaweiler und die angrenzenden Stadtteile sind getrennte Einsatzgebiete. Für die korrekte Zuordnung nennen Sie bitte immer den tatsächlichen Ort und die vollständige Anschrift."],
    ],
    officialUrl: "https://www.dueren.de/kultur-tourismus/stadtportraet/stadtteile/mariaweiler",
  },
  {
    path: "/schluesseldienst-lendersdorf",
    area: "Lendersdorf",
    travel: "am südlichen Stadtrand Dürens in Richtung Niederau und Kreuzau",
    focus: "Notöffnungen, Zylinderwechsel und Sicherheitsfragen",
    intro:
      "Lendersdorf bildet den südlichen Übergang des Dürener Stadtgebiets in Richtung Niederau und Kreuzau; östlich verläuft die Rur. Türöffnungen und Schlossarbeiten erfolgen hier als mobiler Vor-Ort-Service.",
    localNote:
      "Die Seite kennzeichnet Lendersdorf als Einsatzgebiet, nicht als Firmenstandort. Gerade in Übergangsbereichen ist die vollständige Adresse wichtig, damit Ort und Anfahrt korrekt abgestimmt werden.",
    planning:
      "Lendersdorf geht räumlich in Richtung Niederau und Kreuzau über. Der tatsächliche Ortsteil und die vollständige Postanschrift verhindern Missverständnisse bei der Einsatzplanung; eine pauschale Zeitangabe nur anhand des Ortsnamens wäre nicht belastbar.",
    localFacts: ["Lendersdorf grenzt an Niederau und die Gemeinde Kreuzau.", "Die Rur verläuft östlich des Stadtteils."],
    nearbyLinks: [
      ["Niederau", "/schluesseldienst-niederau"],
      ["Kreuzau", "/schluesseldienst-kreuzau"],
    ],
    faqs: [
      ["Liegt meine Adresse in Lendersdorf, Niederau oder schon in Kreuzau?", "In den Übergangsbereichen ist die vollständige Anschrift entscheidend. Die Zuordnung und mögliche Anfahrt werden vor Auftragserteilung telefonisch geklärt."],
      ["Gibt es eine Filiale in Lendersdorf?", "Nein. Lendersdorf ist ein mobiles Einsatzgebiet. Verfügbarkeit, Vorgehensweise und Kosten werden für den konkreten Auftrag abgestimmt."],
    ],
    officialUrl: "https://www.dueren.de/kultur-tourismus/stadtportraet/stadtteile/lendersdorf",
  },
  {
    path: "/schluesseldienst-niederau",
    area: "Niederau",
    travel: "im offiziellen Stadtteilbereich Niederau-Krauthausen im Süden Dürens",
    focus: "Hilfe bei zugefallenen Türen und defekten Schlössern",
    intro:
      "Niederau gehört zum offiziellen Stadtteilbereich Niederau-Krauthausen im Süden Dürens. Zwischen Wohnbereichen, Rur und Burgauer Wald ist für einen mobilen Einsatz vor allem die genaue Anschrift entscheidend.",
    localNote:
      "Türöffnung, Schlosswechsel und weitere angebotene Leistungen werden telefonisch abgestimmt. Es wird keine Niederlassung in Niederau behauptet.",
    planning:
      "Für Niederau-Krauthausen ist die genaue Ortsangabe besonders wichtig. Bei Gebäuden mit mehreren Eingängen nennen Sie bitte Zufahrt, Eingang, Etage und die betroffene Tür, bevor Verfügbarkeit und Preis vereinbart werden.",
    localFacts: ["Die Stadt führt Niederau und Krauthausen als gemeinsamen Stadtteilbereich.", "Lendersdorf grenzt unmittelbar an Niederau-Krauthausen."],
    nearbyLinks: [
      ["Lendersdorf", "/schluesseldienst-lendersdorf"],
      ["Kreuzau", "/schluesseldienst-kreuzau"],
    ],
    faqs: [
      ["Gilt die Niederau-Seite auch für Krauthausen?", "Die Stadt führt beide Bereiche gemeinsam als Niederau-Krauthausen. Nennen Sie beim Anruf trotzdem den tatsächlichen Ortsteil und die vollständige Adresse."],
      ["Welche Angaben helfen bei mehreren Gebäudeeingängen?", "Benötigt werden Straße, Hausnummer, genauer Eingang, Etage und die betroffene Tür. Vor einer Öffnung muss die Berechtigung nachgewiesen werden."],
    ],
    officialUrl: "https://www.dueren.de/kultur-tourismus/stadtportraet/stadtteile/niederau-krauthausen",
  },
  {
    path: "/schluesseldienst-kreuzau",
    area: "Kreuzau",
    travel: "in der eigenständigen Gemeinde südlich von Düren",
    focus: "Türöffnung, Schlosswechsel und Schlüsselnotdienst",
    intro:
      "Kreuzau ist eine eigenständige Gemeinde südlich von Düren und umfasst mehrere Ortsteile zwischen dem Dürener Stadtgebiet und der Rureifel. Der Schlüsseldienst bietet hier mobilen Service nach telefonischer Abstimmung an.",
    localNote:
      "Eine Filiale in Kreuzau wird nicht behauptet. Für die Einsatzplanung nennen Sie bitte, ob sich die Adresse im Zentralort oder zum Beispiel in Winden, Stockheim oder Drove befindet.",
    planning:
      "Da die Gemeinde mehrere Ortsteile umfasst, wird die mögliche Anfahrt nicht allein unter dem Stichwort Kreuzau zugesagt. Ortsteil, Straße, Hausnummer und Türsituation bilden die Grundlage für die telefonische Einsatzprüfung.",
    localFacts: ["Kreuzau ist keine Dürener Ortslage, sondern eine eigene Gemeinde.", "Das Gemeindegebiet umfasst 15 Ortsteile und reicht in Richtung Rureifel."],
    nearbyLinks: [
      ["Lendersdorf", "/schluesseldienst-lendersdorf"],
      ["Niederau", "/schluesseldienst-niederau"],
    ],
    faqs: [
      ["Bezieht sich „Schlüsseldienst Kreuzau“ nur auf den Zentralort?", "Die Gemeinde besteht aus 15 Ortsteilen. Ob die konkrete Adresse im Einsatzgebiet liegt, wird anhand von Ortsteil, Straße und Hausnummer telefonisch geklärt."],
      ["Gilt die typische Anfahrtszeit überall in Kreuzau?", "Die Entfernung kann innerhalb des Gemeindegebiets je nach Ortsteil und Verkehr abweichen. Eine konkrete Zeit wird deshalb erst nach Angabe der vollständigen Adresse genannt."],
    ],
    officialUrl: "https://www.kreis-dueren.de/kreis-dueren-entdecken/gemeinden/gemeinden/kreuzau.php",
  },
  {
    path: "/schluesseldienst-merzenich",
    area: "Merzenich",
    travel: "in der eigenständigen Gemeinde nordöstlich von Düren",
    focus: "Schlüsselnotdienst und transparente Preisabsprache",
    intro:
      "Merzenich liegt nordöstlich von Düren und ist eine eigenständige Gemeinde mit mehreren Ortschaften. Für Türöffnungen und weitere angebotene Leistungen ist ein mobiler Einsatz nach vorheriger Abstimmung möglich.",
    localNote:
      "In Merzenich wird keine Niederlassung behauptet. Damit die Anfrage korrekt geplant werden kann, nennen Sie bitte Zentralort beziehungsweise Ortsteil und die vollständige Adresse.",
    planning:
      "Bei Merzenich, Girbelsrath, Golzheim und den weiteren Ortschaften können Entfernung und Zufahrt unterschiedlich sein. Die vollständige Anschrift und eine erreichbare Rückrufnummer werden deshalb vor einer verbindlichen Zusage benötigt.",
    localFacts: ["Merzenich grenzt im Südwesten an Düren.", "Zum Gemeindegebiet gehören neben dem Zentralort unter anderem Girbelsrath und Golzheim."],
    nearbyLinks: [
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
      ["Birkesdorf", "/schluesseldienst-birkesdorf"],
    ],
    faqs: [
      ["Ist mit „Merzenich“ nur der Zentralort gemeint?", "Die Gemeinde umfasst mehrere Ortschaften. Nennen Sie beim Anruf deshalb bitte Ortsteil, Straße und Hausnummer, damit das Einsatzgebiet eindeutig geprüft werden kann."],
      ["Bedeutet die Nähe zur A4 eine garantierte kurze Anfahrt?", "Nein. Verkehrslage, Ausgangspunkt und konkrete Adresse können variieren. Eine belastbare Zeitangabe ist erst für den tatsächlichen Auftrag möglich."],
    ],
    officialUrl: "https://www.kreis-dueren.de/kreis-dueren-entdecken/gemeinden/gemeinden/merzenich.php",
  },
  {
    path: "/schluesseldienst-dueren-zentrum",
    area: "Düren Zentrum",
    travel: "rund um Markt, Kaiserplatz und die zentralen Einkaufsstraßen",
    focus: "Türöffnungen für Wohnungen, Häuser und Gewerbe",
    intro:
      "Das Einsatzgebiet Düren Zentrum umfasst die Innenstadt rund um Markt, Kaiserplatz und die zentralen Einkaufsstraßen. Mit der Hauptstelle in der Wirtelstraße 38–42 ist hier eine echte Geschäftsadresse vorhanden.",
    localNote:
      "Bei Häusern mit mehreren Eingängen, Ladenlokalen oder schwer zuzuordnenden Zugängen helfen genaue Angaben zu Straße, Eingang und Etage bei der telefonischen Klärung.",
    planning:
      "In der Innenstadt können Fußgängerbereiche, Hinterhöfe und verschiedene Gebäudeeingänge die Zuordnung erschweren. Nennen Sie einen eindeutig erreichbaren Treffpunkt und bleiben Sie unter der angegebenen Telefonnummer erreichbar.",
    localFacts: ["Wirtelstraße, Kölnstraße und Zehnthofstraße gehören zu den wichtigen Innenstadtverbindungen.", "Nord-Düren mit dem Hauptbahnhof grenzt unmittelbar an die Innenstadt."],
    nearbyLinks: [
      ["Birkesdorf", "/schluesseldienst-birkesdorf"],
      ["Mariaweiler", "/schluesseldienst-mariaweiler"],
    ],
    faqs: [
      ["Gehört der Hauptbahnhof noch zu Düren Zentrum?", "Der Hauptbahnhof liegt im unmittelbar angrenzenden Nord-Düren. Für die korrekte Einsatzzuordnung nennen Sie bitte immer die genaue Straße."],
      ["Welche Angaben sind bei einem Gebäude in der Innenstadt wichtig?", "Neben Hausnummer und betroffener Tür helfen Eingang, Etage und gegebenenfalls der Name des Ladenlokals oder Gebäudeteils. Geöffnet wird nur nach Berechtigungsprüfung."],
    ],
    officialUrl: "https://www.dueren.de/leben-wohnen/bauen-wohnen/stadtentwicklung/masterplan_innenstadt/allgemeines",
  },
  {
    path: "/schluesseldienst-juelich",
    area: "Jülich",
    travel: "in der eigenständigen Stadt rund 19 Kilometer nördlich von Düren",
    focus: "Türöffnungen, Schlosswechsel und Hilfe bei Fahrzeugschlüsseln",
    intro:
      "Jülich liegt rund 19 Kilometer nördlich von Düren im nördlichen Bereich des Kreises. Neben der Kernstadt gehören 15 weitere Stadtteile zum Stadtgebiet; für einen mobilen Einsatz sind deshalb der genaue Stadtteil und die vollständige Anschrift besonders wichtig.",
    localNote:
      "Jülich wird als mobiles Einsatzgebiet beschrieben. Eine Filiale oder Geschäftsadresse in Jülich wird nicht behauptet. Verfügbarkeit, voraussichtliche Anfahrt und Festpreis werden vor einer Beauftragung telefonisch geklärt.",
    planning:
      "Für Jülich ist entscheidend, ob die Adresse in der Kernstadt oder einem der weiteren Stadtteile liegt. Die typische Dürener Anfahrtsangabe wird nicht übertragen; erst die vollständige Anschrift ermöglicht eine realistische Auskunft.",
    localFacts: ["Jülich liegt im nördlichen Teil des Kreises Düren; die Stadt nennt eine Entfernung von 19 Kilometern nach Düren.", "Neben der Kernstadt gehören 15 weitere Stadtteile zum Stadtgebiet.", "Die Rur fließt durch Jülich; über die Rurtalbahn besteht eine direkte Verbindung nach Düren."],
    nearbyLinks: [
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
      ["Birkesdorf", "/schluesseldienst-birkesdorf"],
    ],
    faqs: [
      ["Gibt es eine Niederlassung des Schlüsseldienstes in Jülich?", "Nein. Jülich ist ein mobiles Einsatzgebiet. Ob und wann die konkrete Adresse angefahren werden kann, wird vor Auftragserteilung telefonisch geprüft."],
      ["Gilt die Jülich-Seite nur für die Kernstadt?", "Nein. Jülich besteht aus der Kernstadt und 15 weiteren Stadtteilen. Nennen Sie deshalb bitte Stadtteil, Straße und Hausnummer; erst danach kann der mobile Einsatz für die konkrete Adresse geprüft werden."],
    ],
    officialUrl: "https://www.juelich.de/datenundfakten",
  },
  {
    path: "/schluesseldienst-kerpen",
    area: "Kerpen",
    travel: "östlich bis nordöstlich von Düren in der Kolpingstadt im Rhein-Erft-Kreis",
    focus: "mobile Tür- und Schlossnotfälle nach telefonischer Einsatzprüfung",
    intro:
      "Die Kolpingstadt Kerpen liegt östlich bis nordöstlich von Düren im Rhein-Erft-Kreis und besteht aus mehreren räumlich getrennten Stadtteilen. Für Anfragen aus Kerpen werden Stadtteil, vollständige Adresse und aktuelle Verfügbarkeit vorab telefonisch geprüft.",
    localNote:
      "In Kerpen besteht keine Niederlassung. Die Seite beschreibt ausschließlich ein mobiles Einsatzgebiet. Gerade bei Stadtteilen wie Buir, Horrem, Sindorf oder Türnich ist die genaue Ortsangabe für eine realistische Anfahrtsauskunft entscheidend.",
    planning:
      "Kerpen liegt außerhalb des Dürener Stadtgebiets. Ein Einsatz wird deshalb nur nach Prüfung von Stadtteil, genauer Adresse, aktueller Verfügbarkeit und Anfahrt verbindlich zugesagt; die Dürener Angabe von 15–30 Minuten gilt hier nicht.",
    localFacts: ["Kerpen gehört zum Rhein-Erft-Kreis; das westliche Stadtgebiet grenzt unmittelbar an Merzenich.", "Zum Stadtgebiet gehören unter anderem Buir, Blatzheim, Horrem, Sindorf und Türnich.", "A4 und A61 treffen sich im Autobahnkreuz Kerpen."],
    nearbyLinks: [
      ["Merzenich", "/schluesseldienst-merzenich"],
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
    ],
    faqs: [
      ["Wird jeder Kerpener Stadtteil automatisch angefahren?", "Die mögliche Anfahrt wird für den konkreten Auftrag geprüft. Nennen Sie deshalb Stadtteil, Straße und Hausnummer, bevor Sie den Auftrag erteilen."],
      ["Gilt für Kerpen die typische Dürener Anfahrtszeit?", "Nein. Die Angabe von 15–30 Minuten bezieht sich auf Düren. Für Kerpen werden Verfügbarkeit und voraussichtliche Anfahrt individuell genannt."],
    ],
    officialUrl: "https://www.stadt-kerpen.de/",
  },
  {
    path: "/schluesseldienst-eschweiler",
    area: "Eschweiler",
    travel: "westlich von Düren in der StädteRegion Aachen",
    focus: "Türöffnungen, Schlosswechsel und Autoöffnungen nach telefonischer Abstimmung",
    intro:
      "Eschweiler liegt westlich von Düren am östlichen Rand der StädteRegion Aachen und grenzt dort an den Kreis Düren. Für einen mobilen Einsatz werden deshalb genaue Adresse, Stadtteil, Türsituation und Verfügbarkeit telefonisch abgestimmt.",
    localNote:
      "Eschweiler ist ein mobiles Einsatzgebiet und keine Niederlassung. Ob sich die Adresse beispielsweise im Stadtzentrum, in Dürwiß, Weisweiler oder einem anderen Stadtteil befindet, ist für die Einsatzplanung wichtig.",
    planning:
      "Für Eschweiler werden Stadtteil und vollständige Adresse vor einer Zusage geprüft. Wegen der Lage außerhalb Dürens nennen wir weder pauschal 15–30 Minuten noch eine Filiale vor Ort; maßgeblich ist die konkrete telefonische Abstimmung.",
    localFacts: ["Eschweiler gehört zur StädteRegion Aachen und grenzt im Osten an den Kreis Düren.", "Die Inde durchquert das Stadtgebiet; im Norden liegt der Blausteinsee.", "Zu den offiziell benannten Bereichen gehören unter anderem Innenstadt, Dürwiß, Röhe, Nothberg und Weisweiler."],
    nearbyLinks: [
      ["Mariaweiler", "/schluesseldienst-mariaweiler"],
      ["Düren Zentrum", "/schluesseldienst-dueren-zentrum"],
    ],
    faqs: [
      ["Welche Angaben werden für eine Anfrage aus Eschweiler benötigt?", "Bitte nennen Sie Stadtteil, vollständige Anschrift, Türzustand und Position des Schlüssels. So lassen sich Verfügbarkeit, Vorgehen und Anfahrt vorab einschätzen."],
      ["Gibt es einen Firmenstandort in Eschweiler?", "Nein. Eschweiler wird als mobiles Einsatzgebiet geführt. Es wird weder eine Filiale noch ein separates Google-Unternehmensprofil vor Ort behauptet."],
    ],
    officialUrl: "https://www.staedteregion-aachen.de/de/navigation/staedteregion/staedte-und-gemeinden/eschweiler",
  },
  {
    path: "/schluesseldienst-huertgenwald",
    area: "Hürtgenwald",
    travel: "südwestlich des Dürener Stadtgebiets in der Nordeifel",
    focus: "mobile Türöffnungen und Schlossarbeiten nach genauer Einsatzprüfung",
    intro:
      "Hürtgenwald liegt südwestlich des Dürener Stadtgebiets in der Nordeifel und umfasst 13 Ortsteile auf 88,04 Quadratkilometern. Für einen mobilen Einsatz ist die genaue Ortslage besonders wichtig, weil die Wege zwischen den Ortsteilen deutlich variieren.",
    localNote:
      "Eine Niederlassung in Hürtgenwald wird nicht behauptet. Für Anfragen aus Bergstein, Gey, Kleinhau, Vossenack und den weiteren Ortsteilen werden Verfügbarkeit, Anfahrt und Festpreis anhand der vollständigen Adresse telefonisch geklärt.",
    planning:
      "Die Wege zwischen den Hürtgenwalder Ortsteilen unterscheiden sich deutlich. Ortsteil, Straße, Hausnummer und eine kurze Beschreibung der Zufahrt sind daher nötig, bevor eine mobile Anfahrt oder Zeit verbindlich bestätigt werden kann.",
    localFacts: ["Hürtgenwald umfasst 13 Ortsteile auf einer Fläche von 88,04 Quadratkilometern.", "Eifelwälder und eine auf zahlreiche Ortsteile verteilte Siedlungsstruktur prägen die Gemeinde.", "Beim Forsthaus Jägerhaus liegt mit 566 Metern der höchste Punkt des Kreises Düren."],
    nearbyLinks: [
      ["Kreuzau", "/schluesseldienst-kreuzau"],
      ["Lendersdorf", "/schluesseldienst-lendersdorf"],
    ],
    faqs: [
      ["Bezieht sich „Hürtgenwald“ auf einen einzelnen Ort?", "Nein. Die Gemeinde umfasst 13 Ortsteile. Für die Einsatzprüfung werden deshalb der tatsächliche Ortsteil, Straße und Hausnummer benötigt."],
      ["Gilt die typische Dürener Anfahrtszeit überall in Hürtgenwald?", "Nein. Aufgrund des großen Gemeindegebiets und der unterschiedlichen Lage der Ortsteile kann die Anfahrt variieren. Eine konkrete Einschätzung ist erst nach Angabe der vollständigen Adresse möglich."],
    ],
    officialUrl: "https://www.kreis-dueren.de/kreis-dueren-entdecken/gemeinden/gemeinden/huertgenwald.php",
  },
];

const guidePages = [
  {
    path: "/ratgeber/tueroeffnung-kosten-dueren",
    title: "Was kostet eine Türöffnung in Düren?",
    description: "Ratgeber zu Kosten einer Türöffnung in Düren: ab 79,- €, Festpreis am Telefon und worauf Sie bei Schlüsseldiensten achten sollten.",
    intro: "Die wichtigste Frage im Notfall lautet meistens: Was kostet es insgesamt? Eine einfache Türöffnung startet bei uns ab 79,- €. Der verbindliche Preis für den beschriebenen Auftrag wird vor der Anfahrt vereinbart.",
    sections: [
      ["Zugefallen, abgeschlossen oder defekt", "Eine nur zugefallene Tür ist eine andere Ausgangslage als eine abgeschlossene Tür, ein abgebrochener Schlüssel oder ein beschädigter Zylinder. Diese Unterschiede beeinflussen Werkzeug, Zeit und mögliche Folgeleistungen. Beschreiben Sie deshalb genau, ob der Schlüssel gedreht wurde, innen steckt oder ein Defekt sichtbar ist."],
      ["Was der Startpreis bedeutet", "Der ausgewiesene Betrag ab 79,- € gilt als Startpreis für eine einfache Türöffnung. Er ist kein Lockpreis für beliebige Situationen. Vor der Anfahrt wird anhand Ihrer Angaben ein verbindlicher Festpreis für den beschriebenen Auftrag vereinbart; unklare Material- oder Zusatzarbeiten gehören nicht stillschweigend dazu."],
      ["Anfahrt, Tageszeit und Zuschläge", "Fragen Sie ausdrücklich, ob Anfahrt, Umsatzsteuer und mögliche Zuschläge im genannten Gesamtpreis enthalten sind. Bei unserem Angebot wird der für die geschilderte Situation geltende Festpreis vor dem Losfahren besprochen. Für weiter entfernte Einsatzgebiete wird auch die voraussichtliche Anfahrt individuell geklärt."],
      ["Eine Rechnung muss nachvollziehbar sein", "Auf der Rechnung sollten Anbieter, Datum, Leistungsort, ausgeführte Arbeit, verwendetes Material und vereinbarter Betrag nachvollziehbar sein. Prüfen Sie die Angaben in Ruhe. Ein nachvollziehbarer Beleg hilft auch dann, wenn Vermieter, Hausverwaltung oder Versicherung später Unterlagen benötigen."],
      ["Beispiel ohne erfundene Endsumme", "Eine zugefallene Wohnungstür ohne Defekt kann unter den bestätigten Startpreis fallen. Eine abgeschlossene Tür mit beschädigtem Zylinder und anschließendem Materialtausch ist ein anderer Auftrag. Weil Material und Zustand variieren, nennen wir dafür keine pauschale Beispielsumme ohne echte Prüfung."],
      ["Checkliste vor der Beauftragung", "Notieren Sie den vollständigen Firmennamen, die Telefonnummer, den Gesamtpreis, die enthaltene Anfahrt, mögliche Bedingungen und die voraussichtliche Ankunft. Erteilen Sie den Auftrag erst, wenn die Angaben verständlich sind. Bei Änderungen vor Ort muss zusätzlicher Aufwand erklärt und erneut freigegeben werden."],
    ],
  },
  {
    path: "/ratgeber/tuer-zugefallen-was-tun",
    title: "Tür zugefallen — was tun?",
    description: "Tür zugefallen in Düren? Diese Schritte helfen: Ruhe bewahren, Situation prüfen, seriösen Schlüsseldienst anrufen und Preis vorher klären.",
    intro: "Wenn die Tür zugefallen ist, sollten Sie nichts überstürzen. Prüfen Sie zuerst Gefahr, Ersatzschlüssel und Türzustand. Eine nicht abgeschlossene Tür lässt sich in vielen Fällen schonend öffnen, aber nicht in jedem Einzelfall garantiert.",
    sections: [
      ["1. Akute Gefahr ausschließen", "Ist ein Kind, eine hilflose Person oder ein Tier eingeschlossen, läuft der Herd oder besteht Rauch- beziehungsweise Brandgefahr, rufen Sie zuerst 112. Folgen Sie den Anweisungen der Leitstelle. Ein privater Schlüsseldienst ersetzt in einer unmittelbaren Gefahrenlage keine Feuerwehr oder medizinische Hilfe."],
      ["2. Sichere Alternativen prüfen", "Gibt es einen erreichbaren Ersatzschlüssel bei Familie, Nachbarn oder Hausverwaltung? Prüfen Sie nur gefahrlose Zugänge. Klettern Sie nicht über Balkone, Dächer oder gekippte Fenster und beschädigen Sie keine Gemeinschaftstüren. In einer Mietwohnung kann die Hausverwaltung Hinweise zum zulässigen Vorgehen geben."],
      ["3. Türsituation genau beschreiben", "Wichtig ist die Unterscheidung zwischen zugefallen und abgeschlossen. Teilen Sie außerdem mit, ob innen ein Schlüssel steckt, ein Schlüssel abgebrochen ist, der Zylinder klemmt oder bereits eigene Öffnungsversuche stattgefunden haben. Diese Angaben bestimmen die mögliche Methode und Preisabsprache."],
      ["4. Keine ungeeigneten Werkzeuge verwenden", "Karten, Schraubendreher, Drähte oder Hebelwerkzeuge können Beschlag, Dichtung, Türblatt und Rahmen beschädigen. Auch Internet-Tricks passen nicht zu jeder Tür. Stoppen Sie, bevor aus einer einfachen Öffnung eine Reparatur an Tür oder Schloss wird."],
      ["5. Anbieter und Gesamtpreis prüfen", "Fragen Sie nach vollständigem Firmennamen, Anfahrt, Gesamtpreis und Bedingungen. Bei uns beginnt eine einfache Türöffnung ab 79,- €; der verbindliche Festpreis für die geschilderte Situation wird vor der Anfahrt vereinbart. Halten Sie die Abmachung für sich fest."],
      ["6. Berechtigungsnachweis bereithalten", "Eine Öffnung darf nur für berechtigte Personen erfolgen. Halten Sie Ausweis, Mietnachweis oder eine andere geeignete Bestätigung bereit. Liegen Dokumente in der Wohnung, kann die Identität unmittelbar nach der Öffnung kontrolliert werden."],
    ],
  },
  {
    path: "/ratgeber/schluessel-verloren-schloss-wechseln",
    title: "Schlüssel verloren — Schloss wechseln?",
    description: "Schlüssel verloren in Düren: Wann ein Schlosswechsel sinnvoll ist und wie Sie sicher entscheiden.",
    intro: "Ein verlorener Schlüssel kann ein Sicherheitsrisiko sein, muss aber nicht automatisch einen Komplettaustausch bedeuten. Entscheidend ist, ob der Schlüssel einer Adresse oder Schließanlage zugeordnet werden kann und wie realistisch ein Missbrauch ist.",
    sections: [
      ["Risiko realistisch einschätzen", "Wurde nur ein einzelner, nicht gekennzeichneter Schlüssel verloren, ist das Risiko anders als bei einem Verlust zusammen mit Ausweis, Tasche oder Unterlagen zur Adresse. Notieren Sie Zeitpunkt und möglichen Verlustort und prüfen Sie, ob weitere Zugänge, Keller oder Briefkasten betroffen sind."],
      ["Diebstahl anders behandeln als Verlegen", "Besteht der Verdacht auf Diebstahl, sichern Sie Belege und prüfen Sie, ob eine Anzeige sinnvoll oder von einer Versicherung verlangt wird. Ändern Sie keine Tatsachen in der Dokumentation. Bei unmittelbarem Einbruchsverdacht wenden Sie sich an die Polizei und betreten Sie gefährdete Räume nicht unbedacht."],
      ["Mietwohnung und Hausverwaltung", "Mieter sollten Vermieter oder Hausverwaltung informieren, besonders wenn eine zentrale Schließanlage betroffen sein könnte. Wer die Kosten trägt, hängt vom Einzelfall und den vertraglichen Umständen ab. Dieser Ratgeber ersetzt keine Rechtsberatung; lassen Sie Zuständigkeiten bei Unsicherheit klären."],
      ["Zylinderwechsel statt Komplettschloss", "Bei vielen Türen genügt der Austausch des Profilzylinders. Ob Beschlag oder Einsteckschloss ebenfalls betroffen sind, muss geprüft werden. Ein unnötiger Komplettaustausch erhöht Kosten, ohne automatisch mehr Sicherheit zu schaffen."],
      ["Ersatzschlüssel und Schließanlage", "Bei einer Schließanlage kann ein verlorener Schlüssel mehrere Türen betreffen. Hersteller, Hausverwaltung oder Anlagenverantwortliche müssen gegebenenfalls einbezogen werden. Vorhandene Sicherungskarten, Schlüsselnummern und Unterlagen helfen bei der technischen Prüfung."],
      ["Passendes Material auswählen", "Zylinderlänge, Türstärke, Schutzbeschlag und gewünschte Sicherheitsmerkmale müssen zusammenpassen. Fragen Sie vor dem Einbau nach Produkt, Maß, Anzahl der Schlüssel und Gesamtpreis. Material sollte erst nach Ihrer Zustimmung eingebaut werden."],
    ],
  },
  {
    path: "/ratgeber/unserioese-schluesseldienste-erkennen",
    title: "Unseriöse Schlüsseldienste erkennen",
    description: "So erkennen Sie unseriöse Schlüsseldienste: Lockpreise, keine Adresse, 0800-Nummern und unklare Zusatzkosten vermeiden.",
    intro: "In einer Aussperrsituation entsteht Zeitdruck. Genau dann helfen einige klare Prüffragen: Wer kommt, wo sitzt das Unternehmen, welcher Gesamtpreis gilt und welche Arbeit ist wirklich nötig? Seriöse Kommunikation ist wichtiger als ein unrealistisch niedriger Werbepreis.",
    sections: [
      ["Lockpreis ist nicht Gesamtpreis", "Extrem niedrige Beträge ohne Erklärung können nur Anfahrt oder eine theoretische Mindestleistung meinen. Fragen Sie nach dem vollständigen Endpreis für die beschriebene Türsituation, einschließlich Anfahrt, Zeitfenster, Umsatzsteuer und möglicher Zuschläge."],
      ["Name, Anschrift und Telefonnummer prüfen", "Ein Anbieter sollte eindeutig benennen, welches Unternehmen den Auftrag annimmt und wer die Rechnung stellt. Prüfen Sie Impressum und erreichbare Geschäftsadresse. Eine Vermittlung muss als solche erkennbar sein; ein Ortsname in einer Anzeige beweist noch keinen lokalen Betrieb."],
      ["Konkrete Fragen am Telefon stellen", "Nennen Sie zugefallen oder abgeschlossen, Schlüsselposition und erkennbare Defekte. Bitten Sie um den vereinbarten Gesamtpreis und die voraussichtliche Anfahrt. Notieren Sie Ansprechpartner, Uhrzeit und zugesagte Leistung. Unklare Antworten sind ein Grund, nicht sofort zu beauftragen."],
      ["Keine unnötige Zerstörung akzeptieren", "Bohren oder der sofortige Austausch von Schloss und Beschlag sind nicht bei jeder zugefallenen Tür notwendig. Lassen Sie sich Methode, Grund und zusätzliche Kosten erklären, bevor irreversible Arbeiten beginnen. Eine pauschale Garantie auf beschädigungsfreie Öffnung wäre allerdings ebenfalls unseriös."],
      ["Vorsicht bei Zahlungsdruck", "Eine nachvollziehbare Rechnung und eine verständliche Leistungsbeschreibung gehören zu einer seriösen Abwicklung. Lassen Sie sich nicht zu einer anderen Summe oder Zahlungsart drängen als vereinbart. Bei Streit sollten Sie Belege, Namen, Uhrzeiten und Fotos sichern und sich fachkundig beraten lassen."],
      ["Bewertungen richtig einordnen", "Viele Sterne allein beweisen noch keine Qualität. Achten Sie auf echte, unterschiedliche Texte, nachvollziehbare lokale Angaben und professionelle Antworten des Betriebs. Vergleichen Sie Unternehmensname, Telefonnummer und Adresse zwischen Website, Google-Profil und Rechnung."],
    ],
  },
];

const allSeoLinks = [
  ...serviceSeoPages.map((page) => ({ label: page.title, path: page.path, group: "Leistung" })),
  ...areaSeoPages.map((page) => ({ label: `Schlüsseldienst ${page.area}`, path: page.path, group: "Ort" })),
  ...guidePages.map((page) => ({ label: page.title, path: page.path, group: "Ratgeber" })),
];

function getRelatedSeoLinks(currentPath) {
  const areaPage = areaSeoPages.find((page) => page.path === currentPath);
  const routeGroups = {
    "/tueroeffnung-dueren": ["/schluesselnotdienst-dueren", "/schlosswechsel-dueren", "/ratgeber/tuer-zugefallen-was-tun", "/ratgeber/tueroeffnung-kosten-dueren", "/schluesseldienst-dueren-zentrum", "/schluesseldienst-birkesdorf"],
    "/schlosswechsel-dueren": ["/tueroeffnung-dueren", "/schluesselnotdienst-dueren", "/ratgeber/schluessel-verloren-schloss-wechseln", "/ratgeber/unserioese-schluesseldienste-erkennen", "/schluesseldienst-dueren-zentrum", "/schluesseldienst-lendersdorf"],
    "/autooeffnung-dueren": ["/autoschluessel-dueren", "/schluesselnotdienst-dueren", "/tueroeffnung-dueren", "/ratgeber/unserioese-schluesseldienste-erkennen", "/schluesseldienst-dueren-zentrum", "/schluesseldienst-merzenich"],
    "/autoschluessel-dueren": ["/autooeffnung-dueren", "/schluesselnotdienst-dueren", "/tueroeffnung-dueren", "/schlosswechsel-dueren", "/schluesseldienst-dueren-zentrum", "/schluesseldienst-kreuzau"],
    "/schluesselnotdienst-dueren": ["/tueroeffnung-dueren", "/schlosswechsel-dueren", "/autooeffnung-dueren", "/ratgeber/tueroeffnung-kosten-dueren", "/schluesseldienst-dueren-zentrum", "/schluesseldienst-birkesdorf"],
    "/ratgeber/tueroeffnung-kosten-dueren": ["/tueroeffnung-dueren", "/schluesselnotdienst-dueren", "/ratgeber/tuer-zugefallen-was-tun", "/ratgeber/unserioese-schluesseldienste-erkennen", "/schluesseldienst-dueren-zentrum"],
    "/ratgeber/tuer-zugefallen-was-tun": ["/tueroeffnung-dueren", "/schluesselnotdienst-dueren", "/ratgeber/tueroeffnung-kosten-dueren", "/schlosswechsel-dueren", "/schluesseldienst-dueren-zentrum"],
    "/ratgeber/schluessel-verloren-schloss-wechseln": ["/schlosswechsel-dueren", "/schluesselnotdienst-dueren", "/tueroeffnung-dueren", "/ratgeber/unserioese-schluesseldienste-erkennen", "/schluesseldienst-dueren-zentrum"],
    "/ratgeber/unserioese-schluesseldienste-erkennen": ["/tueroeffnung-dueren", "/schluesselnotdienst-dueren", "/ratgeber/tueroeffnung-kosten-dueren", "/schlosswechsel-dueren", "/schluesseldienst-dueren-zentrum"],
  };

  const relatedPaths = areaPage
    ? [
        "/tueroeffnung-dueren",
        "/schluesselnotdienst-dueren",
        "/schlosswechsel-dueren",
        "/autooeffnung-dueren",
        ...areaPage.nearbyLinks.map(([, path]) => path),
      ]
    : routeGroups[currentPath] || allSeoLinks.slice(0, 6).map((link) => link.path);

  return relatedPaths
    .filter((path, index, paths) => path !== currentPath && paths.indexOf(path) === index)
    .slice(0, 6)
    .map((path) => allSeoLinks.find((link) => link.path === path))
    .filter(Boolean);
}

function CallLink({ children, className = "", testId, variant = "default", info = staticInfo }) {
  const contact = info || staticInfo;
  return (
    <Button asChild variant={variant} className={className} data-testid={`${testId}-wrapper`}>
      <a href={contact.phone_href} aria-label={`Jetzt ${contact.phone} anrufen`} data-testid={testId}>
        {children}
      </a>
    </Button>
  );
}

function WhatsappLink({ children, className = "", testId, info = staticInfo }) {
  const contact = info || staticInfo;
  return (
    <Button asChild className={className} data-testid={`${testId}-wrapper`}>
      <a href={contact.whatsapp_href} target="_blank" rel="noreferrer" aria-label="WhatsApp Nachricht senden" data-testid={testId}>
        {children}
      </a>
    </Button>
  );
}

function Header({ info }) {
  return (
    <>
      <a className="skip-link" href="#main-content">Zum Hauptinhalt</a>
      <header className="site-header" data-testid="site-header">
        <div className="container header-inner">
          <a href="/" className="brand-mark" data-testid="header-brand-link" aria-label="Zur Startseite">
            <span className="brand-icon" data-testid="header-brand-icon"><KeyRound size={21} /></span>
            <span data-testid="header-brand-name">{info.name}</span>
          </a>
          <nav className="desktop-nav" aria-label="Hauptnavigation" data-testid="desktop-navigation">
            <a href="/#preise" data-testid="nav-prices-link">Preise</a>
            <a href="/leistungen/" data-testid="nav-services-link">Leistungen</a>
            <a href="/#bewertungen" data-testid="nav-reviews-link">Bewertungen</a>
            <a href="/#faq" data-testid="nav-faq-link">FAQ</a>
          </nav>
          <CallLink className="call-button header-call" testId="header-call-button" info={info}>
            <PhoneCall size={18} />
            <span data-testid="header-call-phone">{info.phone}</span>
          </CallLink>
        </div>
      </header>
    </>
  );
}

function Hero({ info }) {
  const doorPrice = info.prices?.[0]?.price || "ab 79,- €";
  return (
    <section className="hero-section" id="top" data-testid="hero-section">
      <div className="hero-bg" aria-hidden="true" />
      <div className="container hero-grid">
        <div className="hero-copy">
          <div className="eyebrow" data-testid="hero-eyebrow"><Sparkles size={17} /> Lokaler Schlüsselnotdienst in {info.city}</div>
          <h1 data-testid="hero-title">Schlüsseldienst Düren</h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            {info.hero_claim} Türöffnung ab 79,- € und ein verbindlicher Festpreis vor der Anfahrt. Rufen Sie direkt an, wenn Sie bei einer zugefallenen Tür, einem defekten Schloss oder einem Autoschlüssel-Problem Hilfe benötigen.
          </p>
          <div className="hero-actions" data-testid="hero-actions">
            <CallLink className="call-button hero-call" testId="hero-call-button" info={info}>
              <PhoneCall size={22} /> Jetzt anrufen: <strong data-testid="hero-call-phone">{info.phone}</strong>
            </CallLink>
            <WhatsappLink className="whatsapp-button hero-whatsapp" testId="hero-whatsapp-button" info={info}>
              <MessageCircle size={21} /> WhatsApp
            </WhatsappLink>
          </div>
          <div className="hero-proof" data-testid="hero-proof-row">
            <span data-testid="hero-proof-address"><MapPin size={16} /> {info.address}</span>
            <span data-testid="hero-proof-fixed"><ShieldCheck size={16} /> Verbindlicher Festpreis vor Anfahrt</span>
          </div>
        </div>
        <div className="hero-panel" data-testid="hero-emergency-panel">
          <div className="rating-card" data-testid="hero-rating-card">
            <div
              className="stars"
              data-testid="hero-stars"
              aria-label={`${info.google_rating} von 5 Sternen bei ${info.google_review_count} Google-Bewertungen`}
            >
              {[1, 2, 3, 4, 5].map((star) => <Star key={star} size={18} fill="currentColor" />)}
            </div>
            <strong data-testid="hero-rating-title">Seriöser Notdienst statt Lockangebot</strong>
            <p data-testid="hero-rating-text">
              Google-Profil: {info.google_rating} von 5 bei {info.google_review_count} öffentlichen Bewertungen. Türsituation schildern, verbindlichen Festpreis vereinbaren und direkte Hilfe erhalten.
            </p>
            <a className="google-link" href={info.google_maps_url} target="_blank" rel="noreferrer" data-testid="hero-google-profile-link">
              Google-Profil ansehen <ExternalLink size={15} />
            </a>
          </div>
          <div className="emergency-card" data-testid="hero-price-card">
            <span data-testid="hero-price-label">Türöffnung</span>
            <strong data-testid="hero-price-value">{doorPrice}</strong>
            <p data-testid="hero-price-note">Verbindlicher Festpreis nach telefonischer Klärung – noch bevor wir losfahren.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="trust-strip" aria-label="Vorteile" data-testid="trust-strip">
      <div className="container stats-grid">
        {trustStats.map(({ value, label, icon: Icon, testId }) => (
          <div className="stat-card" key={value} data-testid={testId}>
            <Icon size={24} data-testid={`${testId}-icon`} />
            <strong data-testid={`${testId}-value`}>{value}</strong>
            <span data-testid={`${testId}-label`}>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Services({ info }) {
  const renderedServices = (info.services?.length ? info.services : services).map((service, index) => ({
    ...services[index],
    ...service,
    icon: services[index]?.icon || KeyRound,
    testId: services[index]?.testId || `service-card-${index + 1}`,
    label: services[index]?.label || "Schnelle Hilfe",
  }));

  return (
    <section className="section" id="leistungen" data-testid="services-section">
      <div className="container">
        <div className="section-heading">
          <span className="section-kicker" data-testid="services-kicker">Unsere Leistungen</span>
          <h2 data-testid="services-title">Leistungen unseres Schlüsseldienstes in Düren</h2>
          <p data-testid="services-description">Kein Formular, kein Warten auf Rückruf. Ein Anruf genügt, um den Einsatz und den Preis sauber zu klären.</p>
        </div>
        <div className="services-grid">
          {renderedServices.map(({ path, title, description, icon: Icon, testId, label }) => (
            <article className="service-card" key={title} data-testid={testId}>
              <div className="service-icon" data-testid={`${testId}-icon`}><Icon size={29} /></div>
              <span className="service-label" data-testid={`${testId}-label`}>{label}</span>
              <h3 data-testid={`${testId}-title`}><a className="service-title-link" href={publicPath(path)}>{title}</a></h3>
              <p data-testid={`${testId}-description`}>{description}</p>
              <a href={info.phone_href} className="text-call-link" data-testid={`${testId}-call-link`}>
                Sofort telefonisch klären <PhoneCall size={16} />
              </a>
            </article>
          ))}
        </div>
        <div className="services-overview-cta">
          <a href="/leistungen/" data-testid="services-overview-link">
            Alle Leistungen im Überblick <ArrowRight size={17} />
          </a>
        </div>
      </div>
    </section>
  );
}

function Pricing({ info }) {
  const renderedPrices = (info.prices?.length ? info.prices : priceRows).map((row, index) => ({
    ...priceRows[index],
    ...row,
    testId: priceRows[index]?.testId || `pricing-row-${index + 1}`,
  }));

  return (
    <section className="section price-section" id="preise" data-testid="pricing-section">
      <div className="container price-grid">
        <div className="price-copy">
          <span className="section-kicker" data-testid="pricing-kicker">Transparente Preise</span>
          <h2 data-testid="pricing-title">Was kostet eine Türöffnung in Düren?</h2>
          <p data-testid="pricing-description">
            Eine einfache Türöffnung startet ab 79,- €. Sie schildern uns Adresse und Türsituation, anschließend vereinbaren wir für den beschriebenen Auftrag einen verbindlichen Festpreis – noch bevor wir losfahren.
          </p>
          <div className="assurance-list" data-testid="pricing-assurance-list">
            <span data-testid="pricing-assurance-hidden"><CheckCircle2 size={18} /> Preis vor der Anfahrt vereinbart</span>
            <span data-testid="pricing-assurance-phone"><CheckCircle2 size={18} /> Verbindlicher Festpreis am Telefon</span>
            <span data-testid="pricing-assurance-local"><CheckCircle2 size={18} /> Lokaler Ansprechpartner in Düren</span>
          </div>
          <nav className="pricing-guide-links" aria-label="Ratgeber zu Kosten und seriösen Schlüsseldiensten">
            <a href="/ratgeber/tueroeffnung-kosten-dueren/">Mehr zu den Kosten einer Türöffnung <ArrowRight size={16} /></a>
            <a href="/ratgeber/unserioese-schluesseldienste-erkennen/">Unseriöse Schlüsseldienste erkennen <ArrowRight size={16} /></a>
          </nav>
        </div>
        <div className="price-table" data-testid="price-table">
          {renderedPrices.map((row) => (
            <div className="price-row" key={row.label} data-testid={row.testId}>
              <div>
                <strong data-testid={`${row.testId}-label`}>{row.label}</strong>
                <span data-testid={`${row.testId}-note`}>{row.note}</span>
              </div>
              <b data-testid={`${row.testId}-price`}>{row.price}</b>
            </div>
          ))}
          <CallLink className="call-button price-call" testId="pricing-call-button" info={info}>
            <PhoneCall size={20} /> Preis jetzt telefonisch klären
          </CallLink>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    {
      icon: PhoneCall,
      title: "Situation schildern",
      description: "Rufen Sie an und nennen Sie die genaue Adresse sowie den Zustand der betroffenen Tür.",
    },
    {
      icon: ShieldCheck,
      title: "Festpreis vereinbaren",
      description: "Vor der Anfahrt erhalten Sie einen verbindlichen Festpreis für den beschriebenen Auftrag.",
    },
    {
      icon: MapPin,
      title: "Anfahrt nach Düren",
      description: "Die typische Anfahrt innerhalb Dürens beträgt 15–30 Minuten. Die voraussichtliche Zeit nennen wir am Telefon.",
    },
    {
      icon: Unlock,
      title: "Berechtigung und Öffnung",
      description: "Vor der Türöffnung prüfen wir Ihre Berechtigung und führen anschließend die vereinbarte Arbeit aus.",
    },
  ];

  return (
    <section className="section process-section" data-testid="process-section">
      <div className="container">
        <div className="section-heading">
          <span className="section-kicker" data-testid="process-kicker">Klarer Ablauf</span>
          <h2 data-testid="process-title">So läuft eine Türöffnung in Düren ab</h2>
          <p data-testid="process-description">Vom ersten Anruf bis zur geöffneten Tür wissen Sie, welcher Schritt als Nächstes folgt.</p>
        </div>
        <ol className="process-grid" data-testid="process-steps">
          {steps.map(({ icon: Icon, title, description }, index) => (
            <li className="process-card" key={title} data-testid={`process-step-${index + 1}`}>
              <div className="process-card-top">
                <span className="process-number" aria-hidden="true">{index + 1}</span>
                <span className="process-icon" aria-hidden="true"><Icon size={24} /></span>
              </div>
              <h3>{title}</h3>
              <p>{description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function LocalTrust({ info }) {
  const localServiceLinks = [
    ["Türöffnung in Düren", "/tueroeffnung-dueren"],
    ["Schlüsselnotdienst Düren", "/schluesselnotdienst-dueren"],
    ["Schlosswechsel in Düren", "/schlosswechsel-dueren"],
    ["Autoöffnung in Düren", "/autooeffnung-dueren"],
  ];

  return (
    <section className="section local-section" data-testid="local-trust-section">
      <div className="container local-card">
        <div className="local-text">
          <span className="section-kicker" data-testid="local-kicker">Vor Ort in Düren</span>
          <h2 data-testid="local-title">Ihr Schlüsseldienst in Düren für Türöffnungen und Schlossarbeiten</h2>
          <p data-testid="local-description">
            Unser Schlüsseldienst unterstützt Kunden in Düren bei zugefallenen oder verschlossenen Türen, defekten Schlössern, verlorenen Schlüsseln und Problemen rund um die Autoöffnung. Vor der Beauftragung werden Situation, Vorgehensweise, Anfahrt und der verbindliche Festpreis telefonisch geklärt.
          </p>
          <p className="local-context" data-testid="local-context">
            Die Hauptstelle liegt in der Wirtelstraße in der Dürener Innenstadt, in fußläufiger Nähe zu Kaiserplatz und Kölnstraße. Die Kölner Landstraße 175 ist als Zweigstelle angegeben. Mobile Einsätze werden telefonisch koordiniert; die 24h-Erreichbarkeit des Notdienstes ist von den Ladenöffnungszeiten zu unterscheiden.
          </p>
          <nav className="local-service-links" aria-label="Schlüsseldienst-Leistungen in Düren">
            {localServiceLinks.map(([label, path]) => (
              <a href={publicPath(path)} key={path}>{label}</a>
            ))}
          </nav>
          <div className="business-addresses" data-testid="business-address-box">
            <div className="address-box">
              <MapPin size={22} />
              <span>
                <small>Hauptstelle</small>
                <strong data-testid="business-main-address">{info.main_address}</strong>
              </span>
            </div>
            <div className="address-box">
              <Building2 size={22} />
              <span>
                <small>Zweigstelle</small>
                <strong data-testid="business-branch-address"><span data-testid="business-address">{info.branch_address}</span></strong>
              </span>
            </div>
          </div>
        </div>
        <div className="area-grid" data-testid="service-area-grid">
          {serviceAreas.map((area) => (
            area.path ? (
              <a href={publicPath(area.path)} key={area.label} data-testid={`service-area-${area.label.toLowerCase().replaceAll(" ", "-")}`}>
                <CheckCircle2 size={16} /> {area.label}
              </a>
            ) : (
              <span key={area.label} data-testid={`service-area-${area.label.toLowerCase().replaceAll(" ", "-")}`}>
                <CheckCircle2 size={16} /> {area.label}
              </span>
            )
          ))}
        </div>
      </div>
    </section>
  );
}

function PaymentTrust() {
  const items = ["EC-Karte", "Kreditkarte"];
  return (
    <section className="payment-section" data-testid="payment-section">
      <div className="container payment-inner">
        <div data-testid="payment-copy">
          <span className="section-kicker" data-testid="payment-kicker">Bezahlung & Sicherheit</span>
          <h2 data-testid="payment-title">Mit EC- oder Kreditkarte bezahlen</h2>
        </div>
        <div className="payment-list" data-testid="payment-list">
          {items.map((item) => (
            <span key={item} data-testid={`payment-option-${item.toLowerCase().replaceAll(" ", "-")}`}><CreditCard size={18} /> {item}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews({ info }) {
  const sliderItems = [...customerVoices, ...customerVoices];
  return (
    <section className="section reviews-section" id="bewertungen" data-testid="reviews-section">
      <div className="container">
        <div className="reviews-heading">
          <div>
            <span className="section-kicker" data-testid="reviews-kicker">Kundenstimmen</span>
            <h2 data-testid="reviews-title">Erfahrungen mit unserem Schlüsseldienst in Düren</h2>
            <p data-testid="reviews-description">
              Ausgewählte echte Kundenstimmen aus Einsätzen in Düren und Umgebung. Das verlinkte Google-Unternehmensprofil zeigt {info.google_rating} von 5 bei {info.google_review_count} öffentlichen Bewertungen (Stand: {info.google_rating_checked_at}).
            </p>
          </div>
          <a href={info.google_maps_url} target="_blank" rel="noreferrer" className="review-google-card" data-testid="reviews-google-profile-link">
            <span data-testid="reviews-google-rating"><Star size={18} fill="currentColor" /> {info.google_rating} von 5</span>
            <strong className="review-google-count" data-testid="reviews-google-count">{info.google_review_count} Google-Bewertungen</strong>
            <small className="review-google-date" data-testid="reviews-google-date">Geprüft am {info.google_rating_checked_at}</small>
            Google-Profil öffnen <ExternalLink size={15} />
          </a>
        </div>
      </div>
      <div className="review-slider" aria-label="Echte Kundenstimmen" data-testid="review-slider">
        <div className="review-track" data-testid="review-slider-track">
          {sliderItems.map((voice, index) => (
            <article
              className="review-card"
              key={`${voice.name}-${index}`}
              data-testid={`review-card-${index + 1}`}
              aria-hidden={index >= customerVoices.length ? "true" : undefined}
              inert={index >= customerVoices.length ? true : undefined}
            >
              <div className="stars review-stars" aria-label="5 Sterne" data-testid={`review-stars-${index + 1}`}>
                {[1, 2, 3, 4, 5].map((star) => <Star key={star} size={15} fill="currentColor" />)}
              </div>
              <p data-testid={`review-text-${index + 1}`}>„{voice.text}“</p>
              <div className="review-person" data-testid={`review-person-${index + 1}`}>
                <strong>{voice.name}</strong>
                <span>{voice.area}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeoLinkHub({ title = "Weitere Leistungen und Einsatzorte", description = "Hier finden Sie zusätzliche Informationen zu unseren Schlüsseldienst-Leistungen in Düren und Umgebung.", currentPath = "" }) {
  const relatedLinks = getRelatedSeoLinks(currentPath);
  return (
    <section className="section seo-hub-section" data-testid="seo-link-hub-section">
      <div className="container">
        <div className="section-heading">
          <span className="section-kicker" data-testid="seo-link-hub-kicker">Mehr Informationen</span>
          <h2 data-testid="seo-link-hub-title">{title}</h2>
          <p data-testid="seo-link-hub-description">{description}</p>
        </div>
        <div className="seo-link-grid" data-testid="seo-link-grid">
          {relatedLinks.map((link, index) => (
            <a className="seo-link-card" href={publicPath(link.path)} key={link.path} data-testid={`seo-link-card-${index + 1}`}>
              <span data-testid={`seo-link-group-${index + 1}`}>{link.group}</span>
              <strong data-testid={`seo-link-label-${index + 1}`}>{link.label}</strong>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImprintContent({ info }) {
  return (
    <article className="legal-card" data-testid="impressum-card">
      <span className="section-kicker" data-testid="impressum-kicker">Impressum</span>
      <h1 data-testid="impressum-title">Angaben gemäß § 5 DDG</h1>
      <div className="legal-facts" data-testid="impressum-facts">
        <p data-testid="impressum-company"><Building2 size={18} /> {info.legal_company}</p>
        <p data-testid="impressum-owner">Inhaber: {info.operator_name}, Einzelunternehmer</p>
        <p data-testid="impressum-main-address">Hauptstelle: {info.main_address}</p>
        <p data-testid="impressum-branch-address">Zweigstelle: {info.branch_address}</p>
        <p data-testid="impressum-phone"><PhoneCall size={18} /> Telefon: <a href={info.phone_href} data-testid="impressum-phone-link">{info.phone}</a></p>
        <p data-testid="impressum-email"><Mail size={18} /> E-Mail: <a href={`mailto:${info.email}`} data-testid="impressum-email-link">{info.email}</a></p>
      </div>
      <h2 data-testid="impressum-responsible-title">Verantwortlich für den Inhalt</h2>
      <p data-testid="impressum-responsible-text">{info.operator_name}, Anschrift wie oben.</p>
      <h2 data-testid="impressum-dispute-title">Verbraucherstreitbeilegung</h2>
      <p data-testid="impressum-dispute-text">Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
    </article>
  );
}

function PrivacyContent({ info }) {
  return (
    <article className="legal-card privacy-card" data-testid="privacy-card">
      <span className="section-kicker" data-testid="privacy-kicker">Datenschutz</span>
      <h1 data-testid="privacy-title">Datenschutzerklärung</h1>
      <p data-testid="privacy-intro">Diese Website ist bewusst kontaktarm aufgebaut: kein Kontaktformular, keine Terminbuchung, keine Kundenkonten. Personenbezogene Daten entstehen vor allem, wenn Sie uns telefonisch, per E-Mail oder über WhatsApp kontaktieren.</p>
      <h2 data-testid="privacy-controller-title">1. Verantwortlicher</h2>
      <p data-testid="privacy-controller-text">Verantwortlich ist {info.operator_name}, {info.legal_company}, {info.main_address}, E-Mail: {info.email}.</p>
      <h2 data-testid="privacy-server-title">2. Server-Logfiles</h2>
      <p data-testid="privacy-server-text">Beim Aufruf der Website können technisch notwendige Zugriffsdaten verarbeitet werden, z. B. IP-Adresse, Datum/Uhrzeit, Browser, Betriebssystem und aufgerufene Seiten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO: sichere und stabile Bereitstellung der Website.</p>
      <h2 data-testid="privacy-contact-title">3. Kontakt per Telefon, E-Mail und WhatsApp</h2>
      <p data-testid="privacy-contact-text">Wenn Sie uns kontaktieren, verarbeiten wir Ihre Angaben zur Bearbeitung Ihrer Anfrage und zur Durchführung vorvertraglicher oder vertraglicher Maßnahmen gemäß Art. 6 Abs. 1 lit. b DSGVO. WhatsApp ist ein externer Dienst; beim Klick verlassen Sie diese Website.</p>
      <h2 data-testid="privacy-external-title">4. Externe Dienste und Links</h2>
      <p data-testid="privacy-external-text">Schriftarten und Websitebilder werden lokal ausgeliefert. Externe Verbindungen entstehen erst, wenn Sie bewusst WhatsApp, E-Mail oder das Google-Unternehmensprofil öffnen. Dann gelten die Datenschutzregeln des jeweiligen Anbieters.</p>
      <h2 data-testid="privacy-rights-title">5. Ihre Rechte</h2>
      <p data-testid="privacy-rights-text">Sie haben nach DSGVO insbesondere Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch. Außerdem können Sie sich bei einer zuständigen Datenschutzaufsichtsbehörde beschweren.</p>
      <p className="legal-note" data-testid="privacy-note">Hinweis: Diese Texte ersetzen keine anwaltliche Rechtsberatung und sollten vor Veröffentlichung final geprüft werden.</p>
    </article>
  );
}

function SiteFooter({ info }) {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="container">
        <div className="footer-inner">
          <div data-testid="footer-business-block">
            <strong data-testid="footer-business-name">{info.name}</strong>
            <span data-testid="footer-business-address">{info.main_address}</span>
            <span className="footer-branch" data-testid="footer-business-branch">Zweigstelle: {info.branch_address}</span>
          </div>
          <div className="footer-links" data-testid="footer-links">
            <a href="/impressum/" data-testid="footer-impressum-link">Impressum</a>
            <a href="/datenschutz/" data-testid="footer-privacy-link">Datenschutz</a>
            <a href={info.google_maps_url} target="_blank" rel="noreferrer" data-testid="footer-google-link">Google-Profil</a>
            <a href={info.phone_href} data-testid="footer-call-link">{info.phone}</a>
          </div>
        </div>

        <div className="footer-link-columns" data-testid="footer-subpage-navigation">
          <div className="footer-link-column" data-testid="footer-services-column">
            <h3 data-testid="footer-services-title">Leistungen</h3>
            <a href="/leistungen/" data-testid="footer-services-overview-link">Alle Leistungen im Überblick</a>
            {serviceSeoPages.map((page, index) => (
              <a href={publicPath(page.path)} key={page.path} data-testid={`footer-service-link-${index + 1}`}>{page.title}</a>
            ))}
          </div>
          <div className="footer-link-column" data-testid="footer-areas-column">
            <h3 data-testid="footer-areas-title">Einsatzorte</h3>
            {areaSeoPages.slice(0, 7).map((page, index) => (
              <a href={publicPath(page.path)} key={page.path} data-testid={`footer-area-link-${index + 1}`}>Schlüsseldienst {page.area}</a>
            ))}
          </div>
          <div className="footer-link-column" data-testid="footer-guides-column">
            <h3 data-testid="footer-guides-title">Ratgeber</h3>
            {guidePages.map((page, index) => (
              <a href={publicPath(page.path)} key={page.path} data-testid={`footer-guide-link-${index + 1}`}>{page.title}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function Faq() {
  return (
    <section className="section faq-section" id="faq" data-testid="faq-section">
      <div className="container faq-grid">
        <div className="section-heading compact">
          <span className="section-kicker" data-testid="faq-kicker">Häufige Fragen</span>
          <h2 data-testid="faq-title">Häufige Fragen zum Schlüsseldienst in Düren</h2>
          <p data-testid="faq-description">Antworten zu Preis, Anfahrt, Türöffnung, Berechtigungsprüfung und Zahlungsmöglichkeiten.</p>
        </div>
        <Accordion type="single" collapsible className="faq-accordion" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem value={`faq-${index}`} key={faq.question} data-testid={`faq-item-${index + 1}`}>
              <AccordionTrigger className="faq-trigger" data-testid={`faq-trigger-${index + 1}`}>{faq.question}</AccordionTrigger>
              <AccordionContent className="faq-content" data-testid={`faq-content-${index + 1}`}>{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCta({ info }) {
  return (
    <section className="final-cta" data-testid="final-cta-section">
      <div className="container final-cta-inner">
        <div>
          <span data-testid="final-cta-kicker">Tür zu? Schlüssel weg?</span>
          <h2 data-testid="final-cta-title">Rufen Sie jetzt {info.name} in {info.city} an.</h2>
          <p data-testid="final-cta-description">Wir klären die Lage direkt am Telefon und nennen Ihnen den Preis transparent vorab.</p>
        </div>
        <CallLink className="call-button final-call" testId="final-call-button" info={info}>
          <PhoneCall size={22} /> {info.phone}
        </CallLink>
      </div>
    </section>
  );
}

function MobileStickyCall({ info }) {
  return (
    <aside className="mobile-sticky-call" aria-label="Direkter Telefonkontakt" data-testid="mobile-sticky-call-bar">
      <a href={info.phone_href} data-testid="mobile-sticky-call-button" aria-label={`Jetzt ${info.phone} anrufen`}>
        <PhoneCall size={20} /> Jetzt anrufen: <strong data-testid="mobile-sticky-phone">{info.phone}</strong>
      </a>
    </aside>
  );
}

function useBusinessInfo() {
  return staticInfo;
}

function useSeo(routePath = "/") {
  const routeConfig =
    routeSeoData.routes.find((route) => route.path === routePath) ||
    routeSeoData.notFound;

  useEffect(() => {
    const { title, description, canonical: canonicalUrl, ogType, robots } = routeConfig.head;
    const socialImage = `${routeSeoData.site.url}/og.jpg`;
    document.documentElement.lang = "de";
    document.title = title;

    const upsertMeta = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    };
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "de_DE" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: staticInfo.name });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: "Schlüsseldienst Düren – 24 Stunden erreichbar unter 0152 5786 8391" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: socialImage });

    document.head.querySelector('meta[name="keywords"]')?.remove();

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);
  }, [routeConfig]);
}

function LegalPageShell({ info, children, testId }) {
  return (
    <div className="landing-page">
      <Header info={info} />
      <MobileStickyCall info={info} />
      <main id="main-content" data-testid={testId}>
        <section className="legal-page-hero" data-testid={`${testId}-hero`}>
          <div className="container">
            <a href="/" className="legal-back-link" data-testid={`${testId}-back-link`}>← Zurück zur Startseite</a>
          </div>
        </section>
        <section className="legal-section legal-page-section" data-testid={`${testId}-content`}>
          <div className="container legal-grid legal-grid-single">
            {children}
          </div>
        </section>
      </main>
      <SiteFooter info={info} />
    </div>
  );
}

function SeoPageShell({ info, children, testId, currentPath, showLinkHub = true }) {
  useSeo(currentPath);
  return (
    <div className="landing-page">
      <Header info={info} />
      <MobileStickyCall info={info} />
      <main id="main-content" data-testid={testId}>
        {children}
        {showLinkHub && <SeoLinkHub currentPath={currentPath} />}
        <FinalCta info={info} />
      </main>
      <SiteFooter info={info} />
    </div>
  );
}

function Breadcrumbs({ items }) {
  return (
    <nav className="container breadcrumbs" aria-label="Brotkrümelnavigation">
      <ol>
        <li><a href="/">Startseite</a></li>
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} aria-current={isCurrent ? "page" : undefined}>
              <span aria-hidden="true">›</span>
              {item.path && !isCurrent ? <a href={publicPath(item.path)}>{item.label}</a> : <span>{item.label}</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ServicesOverviewContent({ info }) {
  return (
    <>
      <section className="seo-page-hero" data-testid="services-overview-hero">
        <Breadcrumbs items={[{ label: "Leistungen" }]} />
        <div className="container seo-page-grid">
          <div>
            <span className="eyebrow" data-testid="services-overview-kicker"><KeyRound size={17} /> Schlüsseldienst-Leistungen in Düren</span>
            <h1 data-testid="services-overview-title">Leistungen unseres Schlüsseldienstes in Düren</h1>
            <p data-testid="services-overview-description">
              Türöffnung, Schlüsselnotdienst, Schlosswechsel, Autoöffnung und Hilfe bei Autoschlüsseln: Wählen Sie die passende Leistung oder rufen Sie im Notfall direkt an.
            </p>
            <div className="hero-actions" data-testid="services-overview-actions">
              <CallLink className="call-button hero-call" testId="services-overview-call-button" info={info}>
                <PhoneCall size={21} /> Direkt anrufen: <strong>{info.phone}</strong>
              </CallLink>
              <WhatsappLink className="whatsapp-button hero-whatsapp" testId="services-overview-whatsapp-button" info={info}>
                <MessageCircle size={21} /> WhatsApp
              </WhatsappLink>
            </div>
          </div>
          <div className="seo-fact-card" data-testid="services-overview-fact-card">
            <span data-testid="services-overview-fact-label">Direkter Kontakt</span>
            <strong data-testid="services-overview-fact-phone">{info.phone}</strong>
            <p data-testid="services-overview-fact-note">24h erreichbar. Verfügbarkeit, Vorgehen und verbindlichen Festpreis vor der Anfahrt telefonisch klären.</p>
          </div>
        </div>
      </section>

      <section className="section" data-testid="services-overview-list">
        <div className="container">
          <div className="section-heading">
            <span className="section-kicker" data-testid="services-overview-list-kicker">Alle Leistungen</span>
            <h2 data-testid="services-overview-list-title">Für jede Situation die passende Hilfe</h2>
            <p data-testid="services-overview-list-description">Jede Leistung besitzt eine eigene Unterseite mit konkreten Informationen zu Ablauf, Einsatz und telefonischer Preisabsprache.</p>
          </div>
          <div className="services-grid services-overview-grid">
            {serviceSeoPages.map((page, index) => {
              const Icon = page.icon;
              return (
                <article className="service-card" key={page.path} data-testid={`services-overview-card-${index + 1}`}>
                  <div className="service-icon" aria-hidden="true"><Icon size={29} /></div>
                  <span className="service-label">{page.keyword}</span>
                  <h3><a className="service-title-link" href={publicPath(page.path)}>{page.title}</a></h3>
                  <p>{page.description}</p>
                  <a className="text-call-link" href={publicPath(page.path)} data-testid={`services-overview-card-link-${index + 1}`}>
                    Leistung ansehen <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section seo-content-section" data-testid="services-overview-guidance">
        <div className="container seo-content-grid">
          <article className="seo-copy-card">
            <h2>Welche Leistung passt zu Ihrer Situation?</h2>
            <p>Ist eine Haus- oder Wohnungstür zugefallen oder abgeschlossen, finden Sie die wichtigsten Hinweise unter Türöffnung. Bei einem verlorenen Schlüssel, einem defekten Schloss oder Sicherheitsbedenken ist die Seite Schlosswechsel die richtige Anlaufstelle.</p>
            <p>Liegt ein Schlüssel im verschlossenen Fahrzeug, führt die Autoöffnung weiter. Bei einem verlorenen oder beschädigten Fahrzeugschlüssel finden Sie die telefonische Ersteinschätzung unter Autoschlüssel-Hilfe.</p>
          </article>
          <aside className="seo-points-card">
            <h2>Vor der Beauftragung</h2>
            <span><CheckCircle2 size={17} /> Situation und genaue Adresse schildern</span>
            <span><CheckCircle2 size={17} /> Verfügbarkeit und Anfahrt klären</span>
            <span><CheckCircle2 size={17} /> Verbindlichen Festpreis vereinbaren</span>
            <span><CheckCircle2 size={17} /> Berechtigungsnachweis bereithalten</span>
          </aside>
        </div>
      </section>
    </>
  );
}

function ServiceSeoContent({ page, info }) {
  const Icon = page.icon;
  return (
    <>
      <section className="seo-page-hero" data-testid="service-seo-hero">
        <Breadcrumbs items={[{ label: "Leistungen", path: "/leistungen" }, { label: page.title }]} />
        <div className="container seo-page-grid">
          <div>
            <span className="eyebrow" data-testid="service-seo-kicker"><Icon size={17} /> {page.keyword}</span>
            <h1 data-testid="service-seo-title">{page.headline}</h1>
            <p data-testid="service-seo-description">{page.description}</p>
            <div className="hero-actions" data-testid="service-seo-actions">
              <CallLink className="call-button hero-call" testId="service-seo-call-button" info={info}>
                <PhoneCall size={21} /> Direkt anrufen: <strong>{info.phone}</strong>
              </CallLink>
              <WhatsappLink className="whatsapp-button hero-whatsapp" testId="service-seo-whatsapp-button" info={info}>
                <MessageCircle size={21} /> WhatsApp
              </WhatsappLink>
            </div>
          </div>
          <div className="seo-fact-card" data-testid="service-seo-fact-card">
            <span data-testid="service-seo-price-label">{page.fact.label}</span>
            <strong data-testid="service-seo-price">{page.fact.value}</strong>
            <p data-testid="service-seo-price-note">{page.fact.note}</p>
          </div>
        </div>
      </section>

      <section className="section seo-content-section" data-testid="service-seo-content">
        <div className="container seo-content-grid">
          <article className="seo-copy-card" data-testid="service-seo-copy">
            {page.paragraphs.map((paragraph, index) => (
              <p key={paragraph} data-testid={`service-seo-paragraph-${index + 1}`}>{paragraph}</p>
            ))}
            <h2 data-testid="service-seo-why-title">Situation direkt und verbindlich klären</h2>
            <p data-testid="service-seo-why-text">Über den direkten Anruf können Standort, Berechtigung, konkrete Situation, Verfügbarkeit und Preis ohne Rückruf-Warteschleife geklärt werden. Notieren Sie die Vereinbarung vor der Beauftragung.</p>

            <div className="service-detail-list" data-testid="service-detail-list">
              {page.details.map(([heading, text], index) => (
                <section className="service-detail" key={heading} data-testid={`service-detail-${index + 1}`}>
                  <h2>{heading}</h2>
                  <p>{text}</p>
                </section>
              ))}
            </div>

            <section className="service-faq" aria-labelledby={`service-faq-${page.path.slice(1)}`}>
              <h2 id={`service-faq-${page.path.slice(1)}`}>Häufige Fragen zu {page.title}</h2>
              {page.faqs.map(([question, answer]) => (
                <article className="service-faq-item" key={question}>
                  <h3>{question}</h3>
                  <p>{answer}</p>
                </article>
              ))}
            </section>
          </article>
          <aside className="seo-points-card" data-testid="service-seo-points-card">
            <h2 data-testid="service-seo-points-title">Wichtige Punkte</h2>
            {page.points.map((point, index) => (
              <span key={point} data-testid={`service-seo-point-${index + 1}`}><CheckCircle2 size={17} /> {point}</span>
            ))}
          </aside>
        </div>
      </section>
    </>
  );
}

function AreaSeoContent({ page, info }) {
  const areaServiceLinks = [
    { label: "Türöffnung", path: "/tueroeffnung-dueren" },
    { label: "Schlosswechsel", path: "/schlosswechsel-dueren" },
    { label: "Autoöffnung", path: "/autooeffnung-dueren" },
    { label: "24h Schlüsselnotdienst", path: "/schluesselnotdienst-dueren" },
  ];

  return (
    <>
      <section className="seo-page-hero" data-testid="area-seo-hero">
        <Breadcrumbs items={[{ label: `Schlüsseldienst ${page.area}` }]} />
        <div className="container seo-page-grid">
          <div>
            <span className="eyebrow" data-testid="area-seo-kicker"><MapPin size={17} /> Mobiler Einsatzservice · 24h erreichbar</span>
            <h1 data-testid="area-seo-title">Schlüsseldienst {page.area}</h1>
            <p data-testid="area-seo-description">{page.intro}</p>
            <div className="hero-actions" data-testid="area-seo-actions">
              <CallLink className="call-button hero-call" testId="area-seo-call-button" info={info}>
                <PhoneCall size={21} /> Jetzt anrufen: <strong>{info.phone}</strong>
              </CallLink>
            </div>
          </div>
          <div className="seo-fact-card" data-testid="area-seo-fact-card">
            <span data-testid="area-seo-local-label">Einsatzgebiet</span>
            <strong data-testid="area-seo-area">{page.area}</strong>
            <p data-testid="area-seo-note">{page.travel}. Schwerpunkt: {page.focus}.</p>
          </div>
        </div>
      </section>

      <section className="section seo-content-section" data-testid="area-seo-content">
        <div className="container seo-content-grid">
          <article className="seo-copy-card" data-testid="area-seo-copy">
            <h2 data-testid="area-seo-service-title">Mobiler Schlüsseldienst für {page.area}</h2>
            <p data-testid="area-seo-paragraph-1">{page.localNote}</p>
            <p data-testid="area-seo-planning">{page.planning}</p>
            <p data-testid="area-seo-paragraph-2">Ob Wohnungstür, Haustür, Fahrzeug oder defektes Schloss: Am Telefon werden Adresse, Türzustand, Berechtigung, Verfügbarkeit und Festpreis vor der Anfahrt geklärt.</p>
            <div className="area-local-facts" aria-label={`Lokale Orientierung für ${page.area}`}>
              {page.localFacts.map((fact) => (
                <p key={fact}><MapPin size={17} aria-hidden="true" /> {fact}</p>
              ))}
            </div>
            <a className="official-area-link" href={page.officialUrl} target="_blank" rel="noreferrer">
              Offizielle Ortsinformation <ExternalLink size={15} />
            </a>
            <h2 data-testid="area-seo-price-title">Preis und Anfahrt vorher klären</h2>
            <p data-testid="area-seo-paragraph-3">
              Die Türöffnung startet ab 79,- €. Für den konkreten Auftrag wird ein verbindlicher Festpreis telefonisch vereinbart. {page.area === "Düren Zentrum"
                ? "Die typische Anfahrt in Düren beträgt 15–30 Minuten und kann je nach Adresse und Verkehr abweichen."
                : `Für ${page.area} werden Verfügbarkeit und voraussichtliche Anfahrt nach Angabe der vollständigen Adresse individuell genannt.`}
            </p>
            <section className="area-faq" aria-labelledby={`area-faq-${page.area.replaceAll(" ", "-")}`}>
              <h2 id={`area-faq-${page.area.replaceAll(" ", "-")}`}>Häufige Fragen zu {page.area}</h2>
              {page.faqs.map(([question, answer]) => (
                <article className="area-faq-item" key={question}>
                  <h3>{question}</h3>
                  <p>{answer}</p>
                </article>
              ))}
            </section>
          </article>
          <aside className="seo-points-card" data-testid="area-seo-points-card">
            <h2 data-testid="area-seo-points-title">Leistungen in {page.area}</h2>
            {areaServiceLinks.map((service, index) => (
              <a href={publicPath(service.path)} key={service.path} data-testid={`area-seo-service-link-${index + 1}`}><CheckCircle2 size={17} /> {service.label}</a>
            ))}
            <h2 className="nearby-title">Nahe Einsatzgebiete</h2>
            {page.nearbyLinks.map(([label, path]) => (
              <a href={publicPath(path)} key={path}><MapPin size={17} /> Schlüsseldienst {label}</a>
            ))}
          </aside>
        </div>
      </section>
    </>
  );
}

function GuideContent({ page, info }) {
  return (
    <>
      <section className="seo-page-hero guide-hero" data-testid="guide-hero">
        <Breadcrumbs items={[{ label: page.title }]} />
        <div className="container seo-page-grid">
          <div>
            <span className="eyebrow" data-testid="guide-kicker"><FileText size={17} /> Ratgeber</span>
            <h1 data-testid="guide-title">{page.title}</h1>
            <p data-testid="guide-description">{page.intro}</p>
            <p className="guide-byline" data-testid="guide-byline">Fachlich geprüft von Schlüsseldienst am Kaufhof · Stand 2. August 2026</p>
            <CallLink className="call-button hero-call" testId="guide-call-button" info={info}>
              <PhoneCall size={21} /> Notfall? Direkt anrufen
            </CallLink>
          </div>
          <div className="seo-fact-card" data-testid="guide-fact-card">
            <span data-testid="guide-fact-label">Kurzantwort</span>
            <strong data-testid="guide-fact-title">Preis vorher klären</strong>
            <p data-testid="guide-fact-note">Bei jedem Schlüsseldienst-Auftrag zuerst Gesamtpreis, Anfahrt und Zuschläge erfragen.</p>
          </div>
        </div>
      </section>
      <section className="section seo-content-section" data-testid="guide-content">
        <div className="container guide-article">
          {page.sections.map(([heading, text], index) => (
            <article className="guide-section-card" key={heading} data-testid={`guide-section-${index + 1}`}>
              <h2 data-testid={`guide-section-title-${index + 1}`}>{heading}</h2>
              <p data-testid={`guide-section-text-${index + 1}`}>{text}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export default function LocksmithLanding() {
  const businessInfo = useBusinessInfo();
  useSeo("/");

  return (
    <div className="landing-page">
      <Header info={businessInfo} />
      <MobileStickyCall info={businessInfo} />
      <main id="main-content" data-testid="locksmith-landing-page">
        <Hero info={businessInfo} />
        <TrustStrip />
        <Services info={businessInfo} />
        <Pricing info={businessInfo} />
        <Process />
        <LocalTrust info={businessInfo} />
        <PaymentTrust />
        <Reviews info={businessInfo} />
        <Faq />
        <FinalCta info={businessInfo} />
      </main>
      <SiteFooter info={businessInfo} />
    </div>
  );
}

export function ImprintPage() {
  const businessInfo = useBusinessInfo();
  useSeo("/impressum");

  return (
    <LegalPageShell info={businessInfo} testId="imprint-page">
      <ImprintContent info={businessInfo} />
    </LegalPageShell>
  );
}

export function PrivacyPage() {
  const businessInfo = useBusinessInfo();
  useSeo("/datenschutz");

  return (
    <LegalPageShell info={businessInfo} testId="privacy-page">
      <PrivacyContent info={businessInfo} />
    </LegalPageShell>
  );
}

export function ServicesOverviewPage() {
  const businessInfo = useBusinessInfo();
  return (
    <SeoPageShell
      info={businessInfo}
      testId="services-overview-page"
      currentPath="/leistungen"
      showLinkHub={false}
    >
      <ServicesOverviewContent info={businessInfo} />
    </SeoPageShell>
  );
}

export function ServiceSeoPage({ slug }) {
  const businessInfo = useBusinessInfo();
  const page = serviceSeoPages.find((item) => item.path === `/${slug}`) || serviceSeoPages[0];
  return (
    <SeoPageShell
      info={businessInfo}
      testId="service-seo-page"
      currentPath={page.path}
    >
      <ServiceSeoContent page={page} info={businessInfo} />
    </SeoPageShell>
  );
}

export function AreaSeoPage({ slug }) {
  const businessInfo = useBusinessInfo();
  const page = areaSeoPages.find((item) => item.path === `/${slug}`) || areaSeoPages[0];
  return (
    <SeoPageShell
      info={businessInfo}
      testId="area-seo-page"
      currentPath={page.path}
    >
      <AreaSeoContent page={page} info={businessInfo} />
    </SeoPageShell>
  );
}

export function GuidePage({ slug }) {
  const businessInfo = useBusinessInfo();
  const page = guidePages.find((item) => item.path === `/ratgeber/${slug}`) || guidePages[0];
  return (
    <SeoPageShell
      info={businessInfo}
      testId="guide-page"
      currentPath={page.path}
    >
      <GuideContent page={page} info={businessInfo} />
    </SeoPageShell>
  );
}

export function NotFoundPage() {
  const businessInfo = useBusinessInfo();
  useSeo("/404");

  return (
    <LegalPageShell info={businessInfo} testId="not-found-page">
      <article className="legal-card">
        <span className="section-kicker">Fehler 404</span>
        <h1>Diese Seite wurde nicht gefunden.</h1>
        <p>Die Adresse ist möglicherweise veraltet oder wurde falsch eingegeben.</p>
        <a href="/" className="legal-back-link">Zur Startseite</a>
      </article>
    </LegalPageShell>
  );
}
